'use client';
// Dossier d'un équipement : fiche signalétique, fiche de vie, pièces jointes,
// étiquette d'inventaire.
//
// La fiche affichait sept champs sur les vingt-cinq que porte le gabarit papier
// du laboratoire. Les rubriques manquantes ne sont pas décoratives : alimentation,
// garantie, contact technique du prestataire, responsable désigné et suppléant
// sont ce qu'on cherche quand un automate tombe en panne un samedi. Les tenir
// hors de l'outil obligeait à rouvrir le classeur Excel — donc à ne jamais le
// mettre à jour.
//
// Ce fichier est séparé d'`Equipements.tsx` (qui garde la liste et la recherche)
// pour la même raison que les écrans « ressources » ont été éclatés : un fichier
// unique de plus de mille lignes rend deux chantiers simultanés impossibles.
import { piecesConnues } from '@/lib/champs-maintenance';
import {
  AIDE_CHAMP, ChoixListe, ChampDuree, ChampPieces, ChampVerification,
  useHabilites, optionsHabilites, etatHabilites, societesDe, techniciensPrestataire,
} from './ChampsMaintenance';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGed, userName } from '@/lib/store';
import { EQUIP_STATES, EQUIP_CATEGORIES, INTERV_TYPES, NC_GRAVITES, CAUSES_PANNE,
  RACCORDEMENTS, CONSTATS_METRO, RATTACHEMENTS_METRO, exigeMetrologie, vocabMeta, fmtDate,
  joursRestants, NATURES_EQUIP, natureEquip, CATEGORIE_LOGICIEL, SIL_RELIE, SIL_SENS, aujourdhuiIso } from '@/lib/meta';
import { VOILE, I, card, inputStyle, EmptyState, ConfirmButton, RefISO, useBusy, useEscape, Selecteur,
  useConfirmation } from '@/components/ui';
import { rubriquesManquantes } from '@/lib/rubriques-equipement';
import { SelecteurPastilles } from '@/components/ui';
import { BoutonJournal } from '@/components/BoutonJournal';
import { ChoixCorrespondant } from '@/components/ChoixCorrespondant';
import { fetchEcriture } from '@/lib/ecrire';
import { VieEquipement } from './VieEquipement';
import { SocieteAffichee, TechniciensAffiches, ChoixContactTechnique, FAMILLES_SOCIETE, type ContactTech } from './ContactsEquipement';
import { lbl, pill } from './commun';
import { useFormulaire } from '@/components/Formulaire';
// MÊME dérivation d'état que la liste et le dossier de l'écran Équipements :
// recalculer l'état ici en donnerait un second, qui finirait par diverger.
import { etatEquip } from '@/shared/miseEnService';
import { useEmplacements } from '@/lib/emplacements';
import { PostesDeLEquipement } from '@/components/screens/PostesEquipements';
import { nomPiece, extensionApercu } from '@/lib/piecesNom';
import { ncsDeLEquipement } from '@/lib/nc-equipement';
import type { PieceAnalysee } from '@/lib/store';
import type { GedEquipement, GedIntervention, GedPieceEquip, GedEtiquette, GedPret, GedMetrologie, ImpactSortieEquip } from '@/lib/types';

import { Bouton } from '@/shared/Bouton';
import { VideoDemonstration } from '@/components/VideoDemonstration';
import { PiedActions } from '@/shared/PiedActions';
import { peutGererParc } from '@/lib/droits-parc';
/** Le nom que la pièce PORTERA, montré avant de verser.
 *
 *  ⚠️ L'extension est déduite du NOM du fichier ici, alors que le serveur la
 *  déduit de la SIGNATURE binaire. C'est le seul écart entre les deux règles, et
 *  il ne porte que sur l'aperçu : un PDF renommé `.jpg` s'afficherait `.jpg` ici
 *  et serait refusé au dépôt, en le disant. La collision n'est pas simulée non
 *  plus — deux pièces de même nom ne se découvrent qu'au moment où le serveur
 *  regarde les pièces déjà au dossier. */
function nomPieceApercu(
  code: string,
  nature: string,
  meta: { reference: string; version: string; dateDocument: string },
  nomFichier: string,
): string {
  return nomPiece(code, {
    nature,
    reference: meta.reference,
    version: meta.version,
    date: meta.dateDocument,
    ext: extensionApercu(nomFichier),
  });
}

/* ------------------------------------------------------------------ outils */

/** Une rubrique non renseignée se DIT, elle ne se laisse pas vide.
 *
 *  Une case blanche est ambiguë : rubrique sans objet, ou information qu'on a
 *  omis de reporter ? Pour une fiche d'identification exigée en 15189 §6.4.2,
 *  la différence est exactement ce qu'un auditeur vient vérifier. */
export function Rubrique({ label, children, mono, large }: {
  label: string; children?: React.ReactNode; mono?: boolean; large?: boolean;
}) {
  const vide = children === null || children === undefined || children === '' || children === false;
  return (
    <div style={{ gridColumn: large ? '1 / -1' : undefined, minWidth: 0 }}>
      <span style={lbl}>{label}</span>
      <div style={{
        fontSize: 12.5, lineHeight: 1.5, wordBreak: 'break-word',
        fontFamily: mono ? 'var(--font-mono)' : undefined,
        color: vide ? 'var(--text-muted)' : 'var(--text-body)',
        fontStyle: vide ? 'italic' : undefined,
      }}>{vide ? 'non renseigné' : children}</div>
    </div>
  );
}

const GRILLE: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px 18px' };

/**
 * UNE FICHE DE LOGICIEL NE PORTE PAS LES MÊMES RUBRIQUES (05/10/2026).
 *
 * « Adapter les champs à cette nouvelle catégorie. » — RQ, 05/10/2026.
 *
 * Trois adaptations, et chacune a sa raison :
 *
 *  1. DES LIBELLÉS CHANGENT, pas les champs. `marque` devient « Éditeur »,
 *     `numeroSerie` devient « N° de licence / clé ». C'est la même donnée au
 *     même endroit — celle qu'on cherche quand le logiciel refuse de démarrer —
 *     et lui donner des colonnes propres aurait dédoublé toutes les listes, les
 *     recherches et les exports du parc pour changer deux mots.
 *
 *  2. DES RUBRIQUES DISPARAISSENT : voltage, ampérage, puissance, année de
 *     fabrication, pays d'origine, emplacement au plan, accessoires. Un logiciel
 *     n'a rien de tout cela. Les afficher « non renseigné » aurait été pire que
 *     de les cacher : sur une fiche d'identification exigée en §6.4.2, une case
 *     vide est ce qu'un auditeur vient vérifier, et sept cases vides par nature
 *     lui apprennent à ne plus les lire.
 *
 *  3. DES RUBRIQUES APPARAISSENT : la version (toujours, même vide — elle est
 *     exigée), les ordinateurs où il tourne, les appareils qu'il pilote.
 *
 * ⚠️ LE DRAPEAU « FICHE INCOMPLÈTE » SUIT, et par le même module des deux côtés
 * (`lib/rubriques-equipement.ts`, jumeau du moteur). Cacher un champ sans
 * changer la liste exigée aurait produit une fiche éternellement incomplète pour
 * des rubriques devenues invisibles — un drapeau qu'on ne peut pas lever.
 */
const estLogiciel = (e: { nature?: string | null; categorie?: string | null }) =>
  natureEquip(e.nature).key === 'logiciel' || String(e.categorie || '').trim() === CATEGORIE_LOGICIEL;

/** Le libellé d'une rubrique sur CETTE fiche : un logiciel a un éditeur et une
 *  licence là où un appareil a une marque et un numéro de série. */
const LIBELLES_LOGICIEL: Record<string, string> = {
  marque: 'Éditeur',
  modele: 'Module / déclinaison',
  numeroSerie: 'N° de licence / clé',
  fournisseur: 'Fournisseur',
};
const libelleRubrique = (cle: string, defaut: string, logiciel: boolean) =>
  (logiciel ? (LIBELLES_LOGICIEL[cle] || defaut) : defaut);

/** Échéance du contrat de maintenance : la date, ET ce qu'elle vaut aujourd'hui.
 *
 *  Une date seule ne se lit pas — « 01/11/2026 » demande un calcul mental que
 *  personne ne fait en passant. Or c'est la seule rubrique de la fiche qui dise
 *  si quelqu'un est encore TENU de venir réparer l'appareil : elle se dit donc
 *  en clair, et elle rougit d'elle-même une fois passée. Le seuil de trois mois
 *  est celui du renouvellement : au-delà, l'alerte crierait toute l'année. */
function EcheanceContrat({ fin }: { fin: string }) {
  const j = joursRestants(fin);
  const date = fmtDate(fin) || fin;
  if (j === null) return <>{date}</>;
  const echu = j < 0;
  const proche = !echu && j <= 92;
  return (
    <>
      {date}
      {(echu || proche) && (
        <span style={{ display: 'block', fontWeight: 700, color: echu ? 'var(--red-600)' : 'var(--amber-600)' }}>
          {echu ? `Échu depuis ${-j} jour${-j > 1 ? 's' : ''}` : `Dans ${j} jour${j > 1 ? 's' : ''}`}
        </span>
      )}
    </>
  );
}

const Bloc = ({ titre, children }: { titre: string; children: React.ReactNode }) => (
  <section style={{ display: 'grid', gap: 10 }}>
    <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-strong)', borderBottom: '1px solid var(--border-subtle)', paddingBottom: 6 }}>{titre}</h3>
    {children}
  </section>
);

/** Case OUI / NON du gabarit. `null` = case laissée vierge : ce n'est pas
 *  « non », c'est « on ne sait pas », et les deux n'ont pas la même portée. */
const OuiNon = ({ v }: { v: boolean | null | undefined }) => {
  if (v === null || v === undefined) return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>non renseigné</span>;
  return <span style={{ fontWeight: 700, color: v ? 'var(--green-600)' : 'var(--red-600)' }}>{v ? 'Oui' : 'Non'}</span>;
};

const octets = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);
const urlFichier = (cle: string, ext: string) => `/api/ged/ged/equip/fichiers/${encodeURIComponent(cle)}/${encodeURIComponent(ext)}`;

/** Lien d'ouverture d'une pièce jointe, avec sa taille — un rapport de 8 Mo sur
 *  une connexion de laboratoire, mieux vaut le savoir avant de cliquer. */
function LienFichier({ f }: { f: { name: string; ext: string; cle: string; size: number } }) {
  return (
    <a href={urlFichier(f.cle, f.ext)} target="_blank" rel="noreferrer"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary-active)', textDecoration: 'none' }}>
      <I n="doc" s={13} />{f.name}
      <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({octets(f.size)})</span>
    </a>
  );
}

/** Bouton d'envoi de fichier : un `<input type=file>` masqué, piloté par un
 *  bouton visible. L'input natif ne se met pas au style du reste de l'écran. */
function BoutonJoindre({ label, onFichier, onFichiers, occupe }: {
  label: string; onFichier?: (f: File) => void;
  /** Sélection MULTIPLE : tous les fichiers choisis d'un coup. */
  onFichiers?: (f: File[]) => void; occupe?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple={!!onFichiers} style={{ display: 'none' }}
        onChange={(e) => {
          const liste = Array.from(e.target.files || []);
          if (onFichiers) { if (liste.length) onFichiers(liste); } else if (liste[0]) onFichier?.(liste[0]);
          e.target.value = '';
        }} />
      <Bouton taille="sm" onClick={() => ref.current?.click()} disabled={occupe}>
        <I n="plus" s={13} />{occupe ? 'Envoi…' : label}
      </Bouton>
    </>
  );
}

/* ------------------------------------------------- 1. fiche signalétique */

export function Identification({ e }: { e: GedEquipement }) {
  const { session, settings } = useGed();
  const [edition, setEdition] = useState(false);
  const [versionHab, setVersionHab] = useState(0);
  const peutParc = peutGererParc(session.roles);
  /* Ce qui manque, calculé sur la fiche qu'on a DÉJÀ en mémoire : pas de second
     appel pour redemander ce que le magasin vient de servir. La liste est le
     jumeau de celle du moteur (`lib/rubriques-equipement.ts`), et une suite les
     compare clé à clé. */
  const manquantes = rubriquesManquantes(e as unknown as Record<string, unknown>);
  const ct = e.contactTechnique;
  const doc = e.documentation;
  /* Une fiche de LOGICIEL ne porte pas les mêmes rubriques : le détail du
     raisonnement est au-dessus de `estLogiciel`. */
  const logiciel = estLogiciel(e);
  // Lot N5-2 — le chemin lisible de l'emplacement. Le code seul (« PRE-ANALYTIQUE-A2-E1 »)
  // ne dit rien à qui cherche l'appareil ; c'est « Pré-analytique › A2 › E1 » qu'on lit.
  const { chemin } = useEmplacements();

  if (edition) return <FormulaireFiche e={e} onFerme={() => setEdition(false)} />;

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {/* Une fiche incomplète est un ÉCART : elle se signale en haut, pas au
          détour d'un champ vide qu'on peut ne pas remarquer. */}
      {/* ⚠️ ON NOMME CE QUI MANQUE (12/09/2026). « Si une fiche équipement est
          incomplète, il faut me montrer quels champs sont incomplets pour lever
          le drapeau » — RQ, 11/09.
          Le bandeau disait « des rubriques exigées manquent », sans lesquelles.
          La liste était pourtant connue du serveur depuis toujours — elle
          n'était simplement jamais transmise. Sur 224 fiches sur 248, ce
          bandeau s'affichait donc en ne disant rien : un signalement qui couvre
          90 % du parc sans jamais dire quoi faire n'est plus un signalement,
          c'est un décor.
          ⚠️ CHAQUE RUBRIQUE PORTE SON POURQUOI, au survol. « Champ obligatoire »
          n'explique rien et se lit comme une contrainte d'outil ; « c'est lui
          qu'on appelle en panne » se comprend, et se remplit. */}
      {e.ficheComplete === false && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.55 }}>
          <strong style={{ color: 'var(--text-strong)' }}>Fiche d’identification incomplète</strong>
          {' '}— <RefISO code="6.4.2" />
          {manquantes.length > 0 ? (
            <>
              {' : '}
              {manquantes.map((r, i) => (
                <span key={r.cle}>
                  {i > 0 && ', '}
                  <strong title={r.pourquoi} style={{ color: 'var(--amber-600)', cursor: 'help' }}>{r.label}</strong>
                </span>
              ))}
              {'.'}
              <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>
                {manquantes.map((r) => <li key={r.cle} style={{ fontSize: 12 }}>{r.label} — {r.pourquoi}</li>)}
              </ul>
            </>
          ) : (
            /* ⚠️ LE DRAPEAU EST POSÉ MAIS RIEN NE MANQUE : cela arrive quand la
               fiche a été complétée hors de cet écran, le drapeau n'ayant été
               recalculé qu'à la prochaine écriture. On le DIT, plutôt que de
               laisser chercher un champ vide qui n'existe pas. */
            <> : les six rubriques sont désormais renseignées. Le drapeau se lèvera au prochain enregistrement de la fiche.</>
          )}
          {peutParc ? ' Complétez-les ici : le drapeau se lève de lui-même.' : ' Signalez-le au responsable qualité.'}
        </div>
      )}
      {/* Le code inscrit sur la fiche papier contredit parfois celui du parc —
          sept cas relevés à la reprise, dont deux fiches portant le même. */}
      {/* ⚠️ La condition porte sur la DIFFÉRENCE, pas sur la présence : le
          message annonçait « différent du code du parc EQ08 » juste après avoir
          affiché EQ08, sur toute fiche portant un identifiant recopié à
          l'identique (recette A12). Un avertissement qui se contredit dans sa
          propre phrase apprend à ne plus lire les avertissements. */}
      {e.codeInventaireFiche && e.codeInventaireFiche.trim() !== (e.code || '').trim() && (
        <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)' }}>
          La fiche papier porte l’identifiant interne{' '}
          <strong style={{ fontFamily: 'var(--font-mono)' }}>{e.codeInventaireFiche}</strong>, différent du code du parc{' '}
          <strong style={{ fontFamily: 'var(--font-mono)' }}>{e.code}</strong>. Codification à trancher.
        </div>
      )}

      <BlocNature e={e} />

      {peutParc && (
        <Bouton taille="sm" onClick={() => setEdition(true)} style={{ width: 'fit-content' }}>
          <I n="pen" s={13} />Compléter la fiche
        </Bouton>
      )}

      <Bloc titre="Identification">
        <div style={GRILLE}>
          <Rubrique label="Code d’inventaire" mono>{e.code}</Rubrique>
          <Rubrique label="Désignation">{e.nom}</Rubrique>
          <Rubrique label="Catégorie">{EQUIP_CATEGORIES.find((c) => c.key === e.categorie)?.label || e.categorie}</Rubrique>
          <Rubrique label={libelleRubrique('marque', 'Marque', logiciel)}>{e.marque}</Rubrique>
          <Rubrique label={libelleRubrique('modele', 'Type / modèle', logiciel)}>{e.modele}</Rubrique>
          <Rubrique label={libelleRubrique('numeroSerie', 'N° de série', logiciel)} mono>{e.numeroSerie}</Rubrique>
          {/* ⚠️ CACHÉES SUR UN LOGICIEL : il n'a ni année de fabrication, ni pays
              d'origine, ni place au plan du laboratoire, ni accessoires. Les
              laisser en « non renseigné » apprendrait à ne plus lire les cases
              vides — or sur une fiche §6.4.2, la case vide est justement ce
              qu'un auditeur vient vérifier. Sa localisation à lui est ailleurs,
              dans « Où il tourne ». */}
          {!logiciel && <Rubrique label="Année de fabrication">{e.anneeFabrication}</Rubrique>}
          {!logiciel && <Rubrique label="Pays d’origine">{e.paysOrigine}</Rubrique>}
          {/* Deux rubriques, volontairement. « Emplacement » est le rattachement
              STRUCTURÉ au plan du laboratoire, cherchable et partagé avec le
              stock et les copies affichées ; « Localisation » est le texte
              libre du document source, gardé tel quel. Les fusionner aurait
              obligé à décider que « Salle biochimie » et « Biochimie »
              désignent le même endroit — une décision du laboratoire, pas de
              l'outil. */}
          {!logiciel && (
            <Rubrique label="Emplacement (plan du laboratoire)">
              {e.emplacementCode ? chemin(e.emplacementCode) : null}
            </Rubrique>
          )}
          {!logiciel && <Rubrique label="Localisation (fiche source)">{e.localisation}</Rubrique>}
          <Rubrique label="Discipline">{e.discipline}</Rubrique>
          {!logiciel && <Rubrique label="Accessoires" large>{e.accessoires}</Rubrique>}
        </div>
      </Bloc>

      {/* ⚠️ UN LOGICIEL N'A PAS D'ALIMENTATION. Le bloc entier disparaît plutôt
          que d'afficher trois « non renseigné » sur chaque fiche logiciel. */}
      {!logiciel && (
        <Bloc titre="Alimentation">
          <div style={GRILLE}>
            <Rubrique label="Voltage">{e.voltage}</Rubrique>
            <Rubrique label="Ampérage">{e.amperage}</Rubrique>
            <Rubrique label="Puissance">{e.puissance}</Rubrique>
          </div>
        </Bloc>
      )}

      {/* OÙ LE LOGICIEL TOURNE — la « localisation » d'un logiciel (§6.4.2). */}
      {logiciel && <BlocOuTourne e={e} />}

      {/* L'INTERFAÇAGE AU SIL — sur TOUTES les fiches. */}
      <BlocSil e={e} />

      {exigeMetrologie(e.categorie) && <BlocMetrologie e={e} />}

      {e.estPret && <BlocPret e={e} />}

      <Bloc titre="Acquisition & garantie">
        <div style={GRILLE}>
          <Rubrique label="Fournisseur">{(e.fournisseurCode || e.fournisseur) ? <SocieteAffichee code={e.fournisseurCode} texte={e.fournisseur} /> : null}</Rubrique>
          <Rubrique label="Date de réception">{fmtDate(e.dateReception) || e.dateReception}</Rubrique>
          <Rubrique label="Mise en service">{fmtDate(e.dateMiseEnService) || e.dateMiseEnService}</Rubrique>
          <Rubrique label="Garantie jusqu’à">{e.garantieJusquA}</Rubrique>
        </div>
      </Bloc>

      {/* LA VERSION. Bloc CONDITIONNEL sur une fiche d'APPAREIL : une pipette et
          un bain-marie n'ont pas de logiciel, et une rubrique « non renseigné »
          sur les 237 fiches du parc apprendrait à ne plus la lire. Les champs,
          eux, sont toujours proposés au formulaire — c'est là qu'on les
          découvre, pas sur la fiche d'un thermomètre.

          ⚠️ TOUJOURS AFFICHÉ SUR UNE FICHE DE LOGICIEL (05/10/2026), même vide.
          « Un logiciel a une version qu'il faut tracer » (RQ, 05/10) : c'est sa
          rubrique d'identification la plus importante, celle qui dit de QUOI les
          vérifications parlent, et elle compte au drapeau « fiche incomplète ».
          Le bloc caché faute de valeur aurait caché précisément le manque. */}
      {(logiciel || e.logiciel?.version || e.logiciel?.edition) && (
        <BlocVersion e={e} logiciel={logiciel} />
      )}

      <Bloc titre="Contrat de maintenance">
        <div style={GRILLE}>
          <Rubrique label="Contrat">{e.contrat ? 'Oui' : null}</Rubrique>
          <Rubrique label="Société">{(e.contrat?.societeCode || e.contrat?.societe) ? <SocieteAffichee code={e.contrat?.societeCode} texte={e.contrat?.societe} /> : null}</Rubrique>
          <Rubrique label="Périodicité contractuelle">{e.contrat?.periode}</Rubrique>
          <Rubrique label="N° / formule">{e.contrat?.type}</Rubrique>
          <Rubrique label="Échéance du contrat">
            {e.contrat?.fin ? <EcheanceContrat fin={e.contrat.fin} /> : null}
          </Rubrique>
          {/* ⚠️ « DE MÉTROLOGIE » SUR UN LOGICIEL NE VEUT RIEN DIRE : un logiciel
              ne se raccorde à rien. Le champ, lui, est le même — c'est la
              périodicité que le plan de maintenance interroge — et seul son
              libellé change. Le renommer pour tout le monde aurait été l'autre
              option, mais « périodicité de métrologie » est le mot du
              laboratoire sur ses 48 fiches de métrologie. */}
          <Rubrique label={logiciel ? 'Périodicité de maintenance' : 'Périodicité de métrologie'}>
            {e.periodiciteMois ? `${e.periodiciteMois} mois` : null}
          </Rubrique>
        </div>
      </Bloc>

      <Bloc titre="Contact technique">
        <div style={GRILLE}>
          <Rubrique label="Société">{(ct?.societeCode || ct?.societe) ? <SocieteAffichee code={ct?.societeCode} texte={ct?.societe} /> : null}</Rubrique>
          {/* Nom et téléphone relus dans l'annuaire (28/10/2026) : c'est là
              qu'on les tient à jour, pas sur chaque fiche d'appareil. */}
          <Rubrique label="Techniciens" large>
            {ct?.techniciens?.length ? <TechniciensAffiches ct={ct} /> : null}
          </Rubrique>
        </div>
      </Bloc>

      <Bloc titre="Documentation & formation">
        <div style={GRILLE}>
          <Rubrique label="Manuel d’utilisation"><OuiNon v={doc?.manuel} /></Rubrique>
          <Rubrique label="Documents techniques"><OuiNon v={doc?.documentsTechniques} /></Rubrique>
          <Rubrique label="Formation utilisateur"><OuiNon v={doc?.formationUtilisateur} /></Rubrique>
          <Rubrique label="Formation maintenance"><OuiNon v={doc?.formationMaintenance} /></Rubrique>
          {/* Un logiciel n'a pas de pièce de rechange. Rubrique retirée plutôt
              que laissée en « non renseigné » : sur une fiche §6.4.2, la case
              vide est ce qu'un auditeur vient vérifier. */}
          {!logiciel && <Rubrique label="Pièce de rechange en stock"><OuiNon v={doc?.pieceRechangeStock} /></Rubrique>}
        </div>
        {doc?.manuel && !(e.pieces || []).some((p) => p.nature === 'notice') && (
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
            La fiche déclare un manuel d’utilisation, mais aucune notice n’est versée au dossier.
            Déposez-la dans l’onglet « Documents » pour qu’elle soit accessible au poste.
          </p>
        )}
      </Bloc>

      {/* Lots N7-3 / N7-4 — la RÉCIPROQUE du rattachement : « qui peut travailler
          sur cet automate ? ». C'est la question qu'un auditeur pose en
          circulant, et elle n'avait aucune réponse à l'écran. Placée juste avant
          « Responsabilités » : le responsable désigné et les postes qui s'en
          servent se lisent ensemble, ou ne se lisent pas. */}
      {/* ⚠️ HABILITATIONS D'ABORD, POSTES ENSUITE (Ines, 28/10/2026) : l'appareil
          exige des habilitations, le poste en exige aussi, et l'appareil sert aux
          postes qui exigent les siennes. Les postes se LISENT donc après les
          habilitations, et se relisent quand on en change une. */}
      <Bloc titre="Habilitations">
        <HabilitationsDeLEquipement documentId={e.documentId} onChange={() => setVersionHab((v) => v + 1)} />
      </Bloc>
      <Bloc titre="Postes de travail">
        <PostesDeLEquipement documentId={e.documentId} version={versionHab} />
      </Bloc>

      <Bloc titre="Responsabilités">
        <div style={GRILLE}>
          <Rubrique label="Responsable">{e.responsableNom}</Rubrique>
          <Rubrique label="Poste">{e.responsablePoste}</Rubrique>
          <Rubrique label="Suppléant">{e.suppleantNom}</Rubrique>
          <Rubrique label="Poste">{e.suppleantPoste}</Rubrique>
          <Rubrique label="Fiche remplie par">{e.ficheRempliePar ? userName(settings, e.ficheRempliePar) : ''}</Rubrique>
          <Rubrique label="Le">{fmtDate(e.ficheRemplieLe) || e.ficheRemplieLe}</Rubrique>
        </div>
      </Bloc>

      {(e.reforme?.date || e.reforme?.lieu || e.reforme?.rapportPar) && (
        <Bloc titre="Mise en réforme">
          <div style={GRILLE}>
            <Rubrique label="Date">{fmtDate(e.reforme.date) || e.reforme.date}</Rubrique>
            <Rubrique label="Lieu">{e.reforme.lieu}</Rubrique>
            <Rubrique label="Rapport établi par" large>{e.reforme.rapportPar}</Rubrique>
          </div>
        </Bloc>
      )}

      {e.observation && (
        <Bloc titre="Observation">
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-body)' }}>{e.observation}</p>
        </Bloc>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LA VERSION, ET SA TRACE
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * L'HISTORIQUE DES VERSIONS — lu du JOURNAL, jamais stocké à part.
 *
 * « Un logiciel a une version qu'il faut tracer. » — RQ, 05/10/2026.
 *
 * ⚠️ LA TRACE EXISTAIT DÉJÀ, elle ne se voyait pas. Chaque correction de la
 * fiche inscrit au journal chaîné « Logiciel — version installée : 6.7.39 →
 * 6.8.0 », avec son auteur et son horodatage signé. Ce qui manquait n'était pas
 * la donnée : c'était de pouvoir la lire sans dépiler cinquante lignes de
 * journal mêlées.
 *
 * ⚠️ ON NE STOCKE DONC AUCUNE LISTE DE VERSIONS. Un second historique, à côté
 * du premier, donnerait deux vérités à tenir d'accord — et la seconde ne serait
 * pas signée. C'est le journal qui fait foi ; ceci n'en est qu'une LECTURE
 * filtrée, et le bouton « Journal » en tête du dossier reste la source.
 *
 * ⚠️ LE FILTRE RECONNAÎT LE LIBELLÉ DU JOURNAL, et c'est sa limite — assumée.
 * S'il ne reconnaît rien, l'écran ne conclut pas « aucun changement » : il
 * renvoie au journal. Affirmer « version jamais modifiée » sur la foi d'un
 * filtre muet serait une conclusion fausse tirée d'une lecture ratée.
 */
function HistoriqueVersions({ journal }: { journal: { ts: string; actorName: string; actor: string; payload: string }[] }) {
  const lignes = React.useMemo(() => journal
    .flatMap((ev) => String(ev.payload || '').split(' · ')
      .filter((seg) => /version\s+install/i.test(seg))
      .map((seg) => ({ ts: ev.ts, par: ev.actorName || ev.actor, texte: seg.trim() })))
    // Du plus récent au plus ancien : on vient voir ce qui a changé en dernier.
    .sort((a, b) => String(b.ts).localeCompare(String(a.ts))), [journal]);

  if (!lignes.length) {
    return (
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
        Aucun changement de version n’apparaît dans ce qui a été relu du journal. Le journal chaîné du
        dossier — bouton <strong>Journal</strong>, en tête — reste la trace qui fait foi : c’est lui
        qu’il faut ouvrir pour en être sûr.
      </p>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <span style={lbl}>Changements de version tracés</span>
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        {lignes.map((l, i) => (
          <div key={`${l.ts}-${i}`} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline', padding: '8px 13px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)', minWidth: 76 }}>
              {fmtDate(l.ts)}
            </span>
            <span style={{ flex: '1 1 200px', fontSize: 12.5, color: 'var(--text-body)' }}>{l.texte}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{l.par}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * LA VERSION — de l'appareil, ou DU logiciel quand la fiche en est un.
 *
 * Un seul bloc pour les deux cas, et un seul champ en base (`logiciel`). C'est
 * la même donnée, relevée au même geste — on lit l'écran « À propos », on note
 * le jour — et tracée au même journal. Lui donner un second champ aurait créé
 * deux rubriques « version » dont il aurait fallu dire laquelle fait foi.
 * Seul le TITRE change : « Logiciel embarqué » décrit une machine,
 * « Version du logiciel » décrit le logiciel lui-même.
 */
function BlocVersion({ e, logiciel }: { e: GedEquipement; logiciel: boolean }) {
  const { events } = useGed();
  const journal = events[e.code || ''] || [];
  const v = e.logiciel;
  return (
    <Bloc titre={logiciel ? 'Version du logiciel' : 'Logiciel embarqué'}>
      <div style={GRILLE}>
        <Rubrique label="Version installée" mono>{v?.version}</Rubrique>
        <Rubrique label="Édition">{v?.edition}</Rubrique>
        <Rubrique label="Version relevée le">{fmtDate(v?.releveLe) || v?.releveLe}</Rubrique>
      </div>
      {/* ⚠️ LA VERSION EXIGÉE SE DIT ICI AUSSI, et pas seulement dans le bandeau
          du haut : sur une fiche de trente rubriques, le bandeau est déjà hors
          de l'écran quand on arrive à ce bloc. */}
      {logiciel && !v?.version && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.55 }}>
          <strong>La version n’est pas relevée.</strong> C’est une rubrique d’identification exigée{' '}
          <RefISO code="6.4.2" /> : sans elle, aucune vérification ne dit de quoi elle parle, et la fiche
          reste marquée incomplète.
        </p>
      )}
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
        La version conditionne ce qui a été vérifié : une vérification de méthode
        vaut pour la version sous laquelle elle a été conduite <RefISO code="7.3.2" />.
        Après une mise à jour, corrigez la version <em>et</em> redatez le relevé.
      </p>
      {logiciel && <HistoriqueVersions journal={journal} />}
    </Bloc>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * OÙ UN LOGICIEL TOURNE
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * LES ORDINATEURS D'UN LOGICIEL — ou « tous », quand il est en ligne.
 *
 * « Un logiciel peut être relié à un ordinateur ou à tous nos ordinateurs s'il
 *  est accessible en ligne. » — RQ, 05/10/2026.
 *
 * ⚠️ C'EST LA LOCALISATION D'UN LOGICIEL, d'où sa place dans l'Identification et
 * non dans un onglet technique : un auditeur demande à VOIR le logiciel tourner,
 * exactement comme il demande à voir un automate dans sa salle.
 *
 * ⚠️ « TOUS » EST UN DRAPEAU, PAS UNE LISTE DE 33 CODES. Cocher les postes un à
 * un donnerait une liste fausse le jour où le parc en gagne un — et fausse en
 * silence. Le drapeau se résout à la lecture ; c'est pourquoi cocher « en
 * ligne » VIDE la liste, et l'écran le dit avant de le faire.
 *
 * ⚠️ LES `PC*` SONT PROPOSÉS EN TÊTE, et ce n'est qu'une aide à la saisie. La
 * règle que le moteur tient est la FAMILLE (`informatique`), vérifiable à l'œil
 * dans un export — pas le préfixe du code, qui se trompe dans les deux sens.
 */
function BlocOuTourne({ e }: { e: GedEquipement }) {
  const { equips, smq, session } = useGed();
  const { run, estOccupe } = useBusy();
  /* La confirmation de l'application, jamais `window.confirm` (UI-CONVENTIONS
     §7, règle ESLint `no-alert`). Celle-ci n'est PAS `danger` : cocher « en
     ligne » ne détruit pas une donnée du laboratoire, cela déplace une
     affirmation — mais elle retire une liste saisie à la main, et cela se dit. */
  const { demander, confirmation } = useConfirmation();
  const peutParc = peutGererParc(session.roles);
  const enLigne = e.accesEnLigne === true;
  const codes = e.ordinateursCodes || [];

  const options = React.useMemo(() => (equips || [])
    .filter((x) => x.code && x.categorie === 'informatique' && x.actif !== false)
    // Les ordinateurs d'abord (`PC*`, serveurs compris), le reste ensuite : les
    // 154 fiches informatiques mêlent les postes aux claviers et aux souris.
    .sort((a, b) => {
      const pa = /^PC/i.test(String(a.code)) ? 0 : 1;
      const pb = /^PC/i.test(String(b.code)) ? 0 : 1;
      return pa - pb || String(a.code).localeCompare(String(b.code), 'fr', { numeric: true });
    })
    .map((x) => ({ documentId: String(x.code), code: String(x.code), nom: x.nom })), [equips]);

  const nomDe = (c: string) => (equips || []).find((x) => x.code === c)?.nom || '';

  const enregistrer = (corps: Record<string, unknown>, message: string) =>
    run('ou', () => smq(`ged/equip/${e.documentId}/fiche`, corps, message));

  return (
    <Bloc titre="Où il tourne">
      {!peutParc ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.6 }}>
          {enLigne
            ? <>Accessible <strong>en ligne</strong> — depuis tous les ordinateurs du laboratoire.</>
            : codes.length
              ? <>Installé sur {codes.map((c) => `${c}${nomDe(c) ? ` (${nomDe(c)})` : ''}`).join(', ')}.</>
              : <span style={{ color: 'var(--amber-600)' }}>Personne n’a encore dit où ce logiciel tourne.</span>}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={enLigne} disabled={estOccupe()}
              onChange={(ev) => {
                const v = ev.target.checked;
                void (async () => {
                  /* La bascule VIDE la liste côté moteur. On le dit AVANT, et
                     on le dit en nommant ce qui part : une liste qui disparaît
                     sans un mot se lit comme une perte de saisie. */
                  if (v && codes.length) {
                    const ok = await demander(
                      `« Accessible en ligne » veut dire « depuis tous les ordinateurs » : les `
                      + `${codes.length} poste(s) désignés (${codes.join(', ')}) seront retirés, et la `
                      + 'fiche dira « tous ». Si l’accès est en réalité limité à ces postes, ne cochez pas.',
                      { titre: 'Déclarer accessible en ligne', confirmer: 'Déclarer « tous »' },
                    );
                    if (!ok) return;
                  }
                  await enregistrer({ accesEnLigne: v, ...(v ? { ordinateursCodes: [] } : {}) },
                    v ? 'Déclaré accessible en ligne, depuis tous les ordinateurs.'
                      : 'Accès par ordinateurs désignés.');
                })();
              }} />
            <span>
              <strong>Accessible en ligne</strong> — donc depuis <strong>tous</strong> les ordinateurs du
              laboratoire, sans être installé sur aucun en particulier (outil consulté au navigateur,
              service hébergé).
            </span>
          </label>

          {enLigne ? (
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
              La liste des ordinateurs est <strong>sans objet</strong> tant que cette case est cochée :
              « tous » est une règle, pas une énumération — elle reste juste le jour où le parc gagne un poste.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: 5 }}>
              <span style={lbl}>Ordinateurs où il est installé</span>
              <SelecteurPastilles mono modifiable unite="ordinateur"
                ariaLabel="Ordinateurs sur lesquels ce logiciel est installé"
                placeholder="Choisir un ordinateur…"
                vide="Personne n’a encore dit où ce logiciel tourne — c’est une rubrique d’identification qui manque."
                options={options}
                choisis={codes}
                onChange={(v) => void enregistrer({ ordinateursCodes: v }, 'Ordinateurs enregistrés.')} />
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Les postes et serveurs (<span style={{ fontFamily: 'var(--font-mono)' }}>PC…</span>) sont
                proposés en tête. Seules les fiches de famille <strong>Informatique / SIL</strong> sont
                acceptées : un logiciel tourne sur un ordinateur, il ne s’installe pas sur un automate.
                Ce qu’il <em>pilote</em>, lui, se déclare plus haut — « Appareils pilotés », sous la nature.
              </p>
            </div>
          )}
          {estOccupe('ou') && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Enregistrement…</span>}
        </div>
      )}
      {confirmation}
    </Bloc>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * INTERFAÇAGE AU SIL — toutes natures
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * RELIÉ, OU NON, AU SYSTÈME INFORMATIQUE DU LABORATOIRE.
 *
 * « Il peut être relié ou non à notre SIL (d'ailleurs ajoute cela dans les
 *  autres équipements également). » — RQ, 05/10/2026.
 *
 * ⚠️ SUR TOUTES LES FICHES, et c'est la demande elle-même : la question part des
 * logiciels mais vaut d'abord des automates — « celui-ci remonte-t-il ses
 * résultats tout seul, ou quelqu'un les recopie-t-il ? » n'avait aucune réponse
 * à l'écran.
 *
 * ⚠️ TROIS ÉTATS, et « sans réponse » est l'état de départ des 248 fiches. Une
 * case vide n'est pas « non » : le jour où l'on compte les appareils non
 * interfacés, confondre les deux donnerait un chiffre que personne n'a affirmé.
 *
 * ⚠️ LE SENS EST EXIGÉ dès que la réponse est « oui », et c'est le moteur qui le
 * tient. « Interfacé » seul ne dit pas où une recopie manuelle subsiste — or
 * c'est exactement là qu'une erreur de transcription peut naître, et c'est ce
 * que la maîtrise des données demande de connaître (§7.6).
 */
function BlocSil({ e }: { e: GedEquipement }) {
  const { smq, session } = useGed();
  const { run, estOccupe } = useBusy();
  const peutParc = peutGererParc(session.roles);
  const sil = e.sil || {};
  const sens = String(sil.sens || '');
  const note = String(sil.note || '');
  /* « OUI » ATTEND SON SENS AVANT DE PARTIR. Le moteur refuse « oui » sans
     sens ; l'envoyer seul faisait tomber ce refus, et la liste du sens — qui
     ne s'affichait qu'une fois « oui » enregistré — n'apparaissait jamais :
     l'impasse que la RQ a rencontrée le 28/10/2026. Le « oui » choisi reste
     donc à l'écran seulement, et part avec le sens. */
  const [ouiEnAttente, setOuiEnAttente] = useState(false);
  const relie = ouiEnAttente ? 'oui' : String(sil.relie || '');

  const envoyer = (bloc: Record<string, unknown>, message: string) =>
    run('sil', () => smq(`ged/equip/${e.documentId}/fiche`, { sil: { relie: String(sil.relie || ''), sens, note, ...bloc } }, message));

  const meta = SIL_SENS[sens];

  if (!peutParc) {
    return (
      <Bloc titre="Interfaçage au SIL">
        <div style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-body)' }}>
          {relie === 'oui'
            ? <>Interfacé — <strong>{meta?.label || sens}</strong>. {meta?.aide}</>
            : relie === 'non'
              ? <>Pas d’interface avec le SIL : les résultats s’y portent à la main.</>
              : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  Personne n’a encore dit si cet équipement est relié au SIL.
                </span>}
          {sil.note ? <div style={{ marginTop: 5, color: 'var(--text-muted)' }}>{sil.note}</div> : null}
        </div>
      </Bloc>
    );
  }

  return (
    <Bloc titre="Interfaçage au SIL">
      <div style={GRILLE}>
        <div>
          <label style={lbl} htmlFor={`sil-r-${e.documentId}`}>Relié au SIL</label>
          <Selecteur id={`sil-r-${e.documentId}`} value={relie} style={inputStyle}
            onChange={(v) => {
              /* Repasser à « non » ou « sans réponse » RETIRE le sens : le
                 moteur refuse « non relié, bidirectionnel », et laisser l'écran
                 l'envoyer ferait tomber un refus sur un geste qui paraît
                 anodin. */
              if (v === 'oui' && !sens) { setOuiEnAttente(true); return; }
              setOuiEnAttente(false);
              void envoyer({ relie: v, sens: v === 'oui' ? sens : '' },
                v === 'oui' ? 'Interfaçage enregistré — précisez le sens.'
                  : v === 'non' ? 'Déclaré non interfacé.' : 'Réponse retirée.');
            }}>
            {SIL_RELIE.map((o) => <option key={o.key || 'vide'} value={o.key}>{o.label}</option>)}
          </Selecteur>
        </div>
        {relie === 'oui' && (
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl} htmlFor={`sil-s-${e.documentId}`}>
              Sens de l’interfaçage <span style={{ color: 'var(--red-600)' }}>*</span>
            </label>
            <Selecteur id={`sil-s-${e.documentId}`} value={sens} style={inputStyle}
              onChange={(v) => {
                if (!v) return;
                /* Le « oui » en attente ne se lâche qu'une fois ÉCRIT : sur un
                   refus, la liste du sens reste à l'écran pour réessayer. */
                void run('sil', async () => {
                  const ok = await smq(`ged/equip/${e.documentId}/fiche`,
                    { sil: { relie: 'oui', sens: v, note } }, 'Interfaçage enregistré.');
                  if (ok) setOuiEnAttente(false);
                });
              }}>
              <option value="">— à préciser —</option>
              {Object.entries(SIL_SENS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </Selecteur>
            <div style={{ fontSize: 12.5, color: meta ? 'var(--text-muted)' : 'var(--amber-600)', marginTop: 4, lineHeight: 1.5 }}>
              {meta
                ? meta.aide
                : 'Précisez le sens : « interfacé » seul ne dit pas où une recopie manuelle subsiste, '
                  + 'et c’est précisément là qu’une erreur de transcription peut naître.'}
            </div>
          </div>
        )}
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={lbl} htmlFor={`sil-n-${e.documentId}`}>Précisions</label>
          {/* ⚠️ CHAMP NON CONTRÔLÉ, enregistré à la sortie. Un état local
              resynchronisé par un effet est interdit ici (règle du dépôt :
              `setState` dans un effet déclenche des rendus en cascade), et
              enregistrer à chaque frappe ferait une ligne de journal par
              lettre. La `key` porte la valeur du serveur : si la fiche est
              rechargée ailleurs, le champ repart de la valeur en base au lieu
              de garder une saisie abandonnée par-dessus. */}
          <input id={`sil-n-${e.documentId}`} key={`${e.documentId}-${note}`}
            defaultValue={note} style={inputStyle}
            placeholder="Pilote, port, prestataire qui a posé l’interface…"
            onBlur={(ev) => {
              const v = ev.target.value;
              if (v !== note) void envoyer({ note: v }, 'Précisions enregistrées.');
            }} />
        </div>
      </div>
      {!relie && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
          Laisser « sans réponse » est légitime : ce n’est pas « non ». Les deux ne se comptent pas
          ensemble, et c’est ce qui permet de savoir ce qui reste à vérifier plutôt que d’affirmer à la
          place du laboratoire.
        </p>
      )}
      {estOccupe('sil') && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Enregistrement…</span>}
    </Bloc>
  );
}

/**
 * Nature de l'appareil, et dette de saisie sur son code.
 *
 * ── Pourquoi la nature n'est pas une rubrique de la fiche ───────────────────
 * `nature` ne DÉCRIT pas l'appareil : elle COMMANDE ce qu'il peut porter. Un
 * équipement de mesure produit un résultat, donc lui seul peut recevoir une
 * carte de contrôle interne, une calibration, un raccordement métrologique. La
 * poser dans le formulaire, entre « Voltage » et « Pays d'origine », en ferait
 * un libellé de plus qu'on change sans y penser — alors qu'un déclassement
 * retire des droits, et que le serveur le refuse tant que la fiche porte encore
 * un de ces éléments. Elle a donc son propre bloc, avec sa conséquence écrite.
 *
 * ── Pourquoi la régularisation vit ici aussi ────────────────────────────────
 * Le code provisoire est une DETTE : l'outil a fabriqué un code faute de code
 * au classeur, ou la désignation reste à préciser. Sa sortie est un geste
 * unique — quelqu'un va lire l'étiquette de l'appareil et saisit le vrai code.
 * Le mettre ailleurs que sur la fiche qu'on a justement sous les yeux
 * garantirait qu'il n'est jamais fait.
 */
/**
 * QUELS APPAREILS CE LOGICIEL COMMANDE — et « aucun » est une réponse.
 *
 * ⚠️ UNE LISTE VIDE N'EST PAS UN MANQUE. « Il y a aussi les logiciels stand
 * alone qui ne sont pas forcément reliés à un équipement, comme un
 * convertisseur ou un outil d'aide à l'interprétation » (RQ, 11/09/2026).
 * L'écran le DIT en toutes lettres, faute de quoi un logiciel autonome
 * paraîtrait éternellement à moitié saisi — et quelqu'un finirait par lui
 * inventer un appareil pour faire disparaître le blanc.
 *
 * ⚠️ ON NE PROPOSE PAS LES AUTRES LOGICIELS. Un logiciel n'en pilote pas un
 * autre : ce que cette rubrique relie, ce sont des machines.
 */
function AppareilsPilotes({ e, peutParc }: { e: GedEquipement; peutParc: boolean }) {
  const { equips, smq } = useGed();
  const { run, estOccupe } = useBusy();
  const codes = ((e as unknown as { equipementsPilotes?: string[] }).equipementsPilotes) || [];
  const options = (equips || [])
    .filter((x) => x.code && x.nature !== 'logiciel' && x.actif !== false && x.documentId !== e.documentId)
    .sort((a, b) => String(a.code).localeCompare(String(b.code)))
    .map((x) => ({ documentId: String(x.code), code: String(x.code), nom: x.nom }));

  if (!peutParc) {
    return (
      <div style={{ fontSize: 12.5 }}>
        <span style={lbl}>Appareils pilotés</span>{' '}
        {codes.length
          ? codes.join(' · ')
          : <span style={{ color: 'var(--text-muted)' }}>aucun — logiciel autonome.</span>}
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 5 }}>
      <span style={lbl}>Appareils pilotés</span>
      <SelecteurPastilles mono modifiable unite="appareil"
        ariaLabel="Appareils que ce logiciel pilote"
        placeholder="Choisir un appareil…"
        vide="Aucun — logiciel autonome. C’est une réponse, pas un manque."
        options={options}
        choisis={codes}
        onChange={(v) => void run('pilotes', () => smq(
          `ged/equip/${e.documentId}/fiche`,
          { equipementsPilotes: v },
          'Appareils pilotés enregistrés.'))} />
      {estOccupe('pilotes') && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Enregistrement…</span>}
    </div>
  );
}

function BlocNature({ e }: { e: GedEquipement }) {
  const { session, smq } = useGed();
  const peutParc = peutGererParc(session.roles);
  const nat = natureEquip(e.nature);
  const [saisieCode, setSaisieCode] = useState('');
  const { run, estOccupe } = useBusy();

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {/* TROIS natures, donc une liste et non plus une bascule à deux états.
          Le bouton « Déclarer accessoire » ne pouvait proposer que le contraire
          de l'état courant : avec trois valeurs, il aurait fallu deviner
          laquelle — et la troisième serait restée inatteignable à l'écran. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5 }}>
        <span style={lbl}>Nature</span>
        <strong style={{ color: 'var(--text-strong)' }}>{nat.titre}</strong>
        {peutParc && (
          <select
            aria-label="Nature de l’équipement"
            value={nat.key} disabled={estOccupe()}
            onChange={(ev) => {
              const cible = ev.target.value;
              if (cible === nat.key) return;
              const meta = natureEquip(cible);
              /* ⚠️ LA FAMILLE SUIT LA NATURE SUR L'AXE LOGICIEL (05/10/2026).
                 Les deux sont COUPLÉES et le moteur refuse la contradiction :
                 envoyer `nature` seule ferait tomber un refus sur un geste qui
                 paraît anodin — « déclaré logiciel » suivi de « cette fiche est
                 de nature LOGICIEL mais d'une autre famille ». L'écran pose donc
                 les deux dans le même envoi, et le dit dans son message.
                 ⚠️ EN SORTANT de `logiciel`, la famille ne peut pas rester
                 « Logiciel » — mais l'outil ne sait pas laquelle choisir. Il
                 remet `informatique`, la plus proche, et la correction se fait
                 au formulaire : une famille devinée se corrige, une
                 contradiction bloque. */
              const corps: Record<string, unknown> = { nature: cible };
              if (cible === CATEGORIE_LOGICIEL) corps.categorie = CATEGORIE_LOGICIEL;
              else if (nat.key === 'logiciel') corps.categorie = 'informatique';
              void run('nature', () => smq(
                `ged/equip/${e.documentId}/fiche`,
                corps,
                cible === CATEGORIE_LOGICIEL
                  ? 'Déclaré « logiciel » — la famille de la fiche passe à « Logiciel ».'
                  : nat.key === 'logiciel'
                    ? `Déclaré « ${meta.court} » — la famille passe à « Informatique / SIL » ; corrigez-la si besoin dans « Compléter la fiche ».`
                    : `Déclaré « ${meta.court} ».`));
            }}
            style={{ ...inputStyle, width: 'auto', fontSize: 12.5, padding: '4px 8px' }}>
            {NATURES_EQUIP.map((n) => <option key={n.key} value={n.key}>{n.titre}</option>)}
          </select>
        )}
      </div>
      {/* ⚠️ La liste comptait TROIS natures et en compte QUATRE depuis le
          12/09/2026 : le commentaire ci-dessus vaut toujours, et c'est bien
          pour cela qu'il fallait une liste — le bouton à deux états n'aurait
          jamais pu proposer la quatrième. */}
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>{nat.aide}</div>

      {/* ── LES APPAREILS QU'UN LOGICIEL PILOTE (026, 12/09/2026) ────────────
          « Les logiciels qui pilotent les machines doivent être reliés à ces
           machines ; par exemple le SCA devra être relié au microscope. »
          ⚠️ LE LIEN SE DÉCLARE ICI, ET NULLE PART AILLEURS. L'onglet
          « Logiciels » d'un appareil ne fait que le LIRE à l'envers. Ouvrir
          l'écriture des deux côtés donnerait deux endroits pour dire la même
          chose, et la question « lequel fait foi » se poserait au premier
          désaccord.
          ⚠️ Le moteur revérifie que chaque code existe au parc : un lien vers un
          code inexistant s'affirmerait ici et resterait invisible là-bas. */}
      {nat.key === 'logiciel' && <AppareilsPilotes e={e} peutParc={peutParc} />}

      {e.codeProvisoire === true && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5 }}>
          <strong style={{ color: 'var(--text-strong)' }}>À régulariser.</strong>{' '}
          Le code <strong style={{ fontFamily: 'var(--font-mono)' }}>{e.code}</strong> a été fabriqué
          par l’outil faute de code au classeur — ou la désignation reste à préciser. Allez lire
          l’étiquette de l’appareil, puis saisissez son code réel : le marquage tombera.
          {peutParc && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input value={saisieCode} onChange={(ev) => setSaisieCode(ev.target.value)}
                placeholder="Code relevé sur l’appareil" aria-label="Code relevé sur l’appareil"
                style={{ ...inputStyle, width: 220, fontFamily: 'var(--font-mono)' }} />
              <Bouton variante="primaire" taille="sm" onClick={() => void run('code', async () => {
                  if (!saisieCode.trim()) return;
                  const ok = await smq(`ged/equip/${e.documentId}/code`, { code: saisieCode.trim() }, 'Fiche régularisée.');
                  if (ok) setSaisieCode('');
                })} disabled={estOccupe() || !saisieCode.trim()}>
                {estOccupe('code') ? 'Enregistrement…' : 'Régulariser'}
              </Bouton>
              {/* La désignation peut avoir été précisée sans que le code change :
                  cette sortie-là existe aussi, sinon la dette resterait pour un
                  motif déjà réglé. */}
              <Bouton taille="sm" onClick={() => void run('leve', () => smq(`ged/equip/${e.documentId}/code`, { provisoire: false }, 'Fiche régularisée.'))} disabled={estOccupe()}>
                {estOccupe('leve') ? '…' : 'Le code est le bon'}
              </Bouton>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Raccordement métrologique d'un équipement intermédiaire — registre J2.
 *
 * L'alerte porte sur ce qui compte : un instrument non raccordé ne peut pas
 * servir de critère de conformité. Le laboratoire coche pourtant déjà
 * « pipette 1000 calibrée » dans sa check-list de passage CQE — un critère qui
 * repose sur un instrument dont l'étalonnage n'est pas tracé ne prouve rien.
 */
export function BlocMetrologie({ e }: { e: GedEquipement }) {
  const m = e.metrologie;
  const raccorde = !!m?.raccordement && m.raccordement !== 'aucun';
  const enService = e.statut === 'en_service';
  const echue = !!e.prochaineEcheance && new Date(e.prochaineEcheance) < new Date();
  // ⚠️ La PIÈCE, pas le numéro. Un `certificatPieceId` qui ne désigne plus rien
  // (document retiré) ne compte pas : c'est le cas qui ferait afficher
  // « documenté » avec un fichier effacé.
  const certificat = (e.pieces || []).find((p) => p.id === m?.certificatPieceId) || null;
  const validite = certificat?.dateValidite || m?.dateValiditeCertificat || '';
  const auj = aujourdhuiIso();
  const certificatPerime = !!validite && validite < auj;

  return (
    <section style={{ display: 'grid', gap: 10 }}>
      <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-strong)', borderBottom: '1px solid var(--border-subtle)', paddingBottom: 6 }}>
        Raccordement métrologique <RefISO code="6.5" />
      </h3>

      {!raccorde && enService && (
        <div style={{ background: 'var(--red-50)', borderInlineStart: '3px solid var(--red-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)' }}>
          <strong style={{ color: 'var(--red-600)' }}>Raccordement non établi.</strong>{' '}
          Tant que l’étalonnage de cet instrument n’est pas raccordé à un étalon
          reconnu, il ne peut pas servir de critère de conformité — ni pour un
          passage de contrôle, ni pour la validation d’une méthode.
        </div>
      )}
      {raccorde && m?.constat === 'non_conforme' && (
        <div style={{ background: 'var(--red-50)', borderInlineStart: '3px solid var(--red-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)' }}>
          <strong style={{ color: 'var(--red-600)' }}>Dernier constat : non conforme.</strong>{' '}
          Déclarez une non-conformité et vérifiez les résultats rendus depuis la dernière
          opération conforme.
        </div>
      )}
      {/* ── Le troisième état, celui que le produit ne savait pas dire ──────
          « Raccordé mais sans pièce » n'est pas une nuance de « raccordé » :
          c'est l'écart d'audit. Il a donc son propre bandeau, distinct de
          « non raccordé » juste au-dessus — les deux appellent des actions
          différentes : faire étalonner, ou retrouver un document. */}
      {raccorde && !certificat && enService && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-strong)' }}>Raccordement déclaré, non documenté.</strong>{' '}
          {m?.refCertificat
            ? `Le certificat ${m.refCertificat} est cité, mais le document n’est pas joint à la fiche.`
            : 'Aucun certificat n’est joint à la fiche.'}{' '}
          Un évaluateur demande la pièce, pas son numéro : joignez-la dans l’onglet
          « Documents », puis désignez-la dans le bloc métrologique.
        </div>
      )}
      {raccorde && certificat && certificatPerime && (
        <div style={{ background: 'var(--red-50)', borderInlineStart: '3px solid var(--red-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--red-600)' }}>Certificat d’étalonnage périmé depuis le {fmtDate(validite)}.</strong>{' '}
          Le raccordement ne tient plus, même si le document est bien archivé.
        </div>
      )}
      {raccorde && certificat && !validite && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-strong)' }}>Certificat joint, sans date de fin de validité.</strong>{' '}
          On ne peut pas dire jusqu’à quand il engage : ce n’est ni valide ni périmé.
        </div>
      )}
      {raccorde && echue && enService && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)' }}>
          <strong style={{ color: 'var(--text-strong)' }}>Échéance d’étalonnage dépassée</strong> — le raccordement n’est plus valable.
        </div>
      )}

      <div style={GRILLE}>
        <Rubrique label="Nature du suivi">
          {m?.suivi ? ({ etalonnage: 'Étalonnage', verification: 'Vérification', aucun: 'Aucun' }[m.suivi] || m.suivi) : null}
        </Rubrique>
        <Rubrique label="Raccordement">
          {m?.raccordement
            ? (
              <span title={RACCORDEMENTS[m.raccordement]?.aide}
                style={{ fontWeight: 700, cursor: 'help', color: m.raccordement === 'aucun' ? 'var(--red-600)' : 'var(--text-body)' }}>
                {RACCORDEMENTS[m.raccordement]?.label || m.raccordement}
              </span>
            )
            : null}
        </Rubrique>
        <Rubrique label="Organisme">{m?.organisme}</Rubrique>
        <Rubrique label="N° d’accréditation" mono>{m?.refAccreditation}</Rubrique>
        <Rubrique label="N° de certificat" mono>{m?.refCertificat}</Rubrique>
        <Rubrique label="Date du certificat">{fmtDate(m?.dateCertificat) || m?.dateCertificat}</Rubrique>
        {/* Le DOCUMENT, avec le lien pour l'ouvrir. C'est ce que l'auditeur
            demande, et c'est ce qui distingue les trois états. */}
        <Rubrique label="Certificat joint">
          {certificat
            ? (
              <a href={`/api/ged/ged/equip/fichiers/${certificat.cle}/${certificat.ext}`} target="_blank" rel="noreferrer"
                className="lien-doux" style={{ color: 'var(--navy-700)' }}>
                {certificat.name || certificat.libelle || 'certificat'}
              </a>
            )
            : (
              <span style={{ color: 'var(--amber-600)', fontWeight: 700 }}>
                non joint — raccordement déclaré, non documenté
              </span>
            )}
        </Rubrique>
        <Rubrique label="Certificat valable jusqu’au">
          {validite
            ? <span style={{ fontFamily: 'var(--font-mono)', color: certificatPerime ? 'var(--red-600)' : undefined, fontWeight: certificatPerime ? 700 : 400 }}>{fmtDate(validite)}</span>
            : null}
        </Rubrique>
        {m?.raccordement === 'interne' && (
          <Rubrique label="Étalon de référence employé" mono>{m?.etalonRef}</Rubrique>
        )}
        {m?.raccordement !== 'interne' && (
          <Rubrique label="Rattachement final">
            {[RATTACHEMENTS_METRO[m?.rattachementNature || '']?.label, m?.rattachementLibelle].filter(Boolean).join(' — ') || null}
          </Rubrique>
        )}
        <Rubrique label="Étendue de mesure">{m?.portee}</Rubrique>
        {/* La plage de conservation d'une enceinte : elle existait en base
            depuis la reprise et n'était affichée nulle part — donc invérifiable
            à l'écran, et c'est ainsi que quatre congélateurs ont porté une plage
            de réfrigérateur pendant deux jours sans que rien ne le montre. */}
        {/* Une enceinte à PLUSIEURS points de surveillance : le combiné
            `CONG-03`. Chaque compartiment porte sa plage et sa source ; les
            fondre en une plage d'appareil est exactement la faute qui avait mis
            « 1-4 °C » sur un congélateur. */}
        {m?.pointsSurveillance?.length ? (
          <Rubrique label="Points de surveillance">
            <div style={{ display: 'grid', gap: 4 }}>
              {m.pointsSurveillance.map((pt) => (
                <div key={pt.code}>
                  <strong style={{ fontSize: 12.5 }}>{pt.libelle}</strong>{' — '}
                  {pt.plageTemperature
                    ? <span title={pt.plageTemperatureSource || undefined} style={{ fontFamily: 'var(--font-mono)' }}>{pt.plageTemperature}</span>
                    : <span style={{ color: 'var(--red-600)', fontWeight: 700 }} title={pt.plageTemperatureAVerifier}>
                        Non fixée — à trancher
                      </span>}
                </div>
              ))}
            </div>
          </Rubrique>
        ) : null}
        {!m?.pointsSurveillance?.length && (m?.plageTemperature || m?.plageTemperatureAVerifier) && (
          <Rubrique label="Plage de conservation">
            {m?.plageTemperature
              ? <span title={m?.plageTemperatureSource || undefined} style={{ fontFamily: 'var(--font-mono)' }}>{m.plageTemperature}</span>
              : <span style={{ color: 'var(--red-600)', fontWeight: 700 }} title={m?.plageTemperatureAVerifier}>
                  Non fixée — à trancher
                </span>}
          </Rubrique>
        )}
        <Rubrique label="Erreur maximale tolérée">{m?.emt}</Rubrique>
        <Rubrique label="Incertitude">{m?.incertitude}</Rubrique>
        <Rubrique label="Dernier constat">
          {m?.constat ? pill(vocabMeta(CONSTATS_METRO, m.constat)) : null}
        </Rubrique>
        <Rubrique label="Dernière opération">{fmtDate(m?.derniereOperation) || m?.derniereOperation}</Rubrique>
        <Rubrique label="Prochaine échéance">
          {e.prochaineEcheance
            ? <span style={{ fontFamily: 'var(--font-mono)', color: echue ? 'var(--red-600)' : undefined, fontWeight: echue ? 700 : 400 }}>{fmtDate(e.prochaineEcheance)}</span>
            : null}
        </Rubrique>
        <Rubrique label="Vérification intermédiaire">
          {m?.verifIntermPeriodiciteMois ? `tous les ${m.verifIntermPeriodiciteMois} mois` : null}
        </Rubrique>
        <Rubrique label="Dernière vérification intermédiaire">{fmtDate(m?.derniereVerifInterm) || m?.derniereVerifInterm}</Rubrique>
        <Rubrique label="Commentaire" large>{m?.commentaire}</Rubrique>
      </div>
    </section>
  );
}

/**
 * Appareil mis à disposition — ce qu'on doit pouvoir dire de lui.
 *
 * L'alerte n'est pas décorative. Un appareil de prêt en service dont l'aptitude
 * n'a pas été vérifiée rend des résultats patients sans qu'on sache s'il en est
 * capable ; c'est précisément ce qui s'est produit sur l'ABL80 en avril 2026.
 * Elle reste affichée tant que l'enregistrement de vérification n'existe pas.
 */
export function BlocPret({ e }: { e: GedEquipement }) {
  const { equips, go, select } = useGed();
  const p = e.pret;
  const titulaire = equips.find((x) => x.code === p?.remplaceCode);
  const rendu = e.statut === 'en_service';
  const restitue = !!p?.dateRestitution;

  return (
    <section style={{ display: 'grid', gap: 10 }}>
      <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-strong)', borderBottom: '1px solid var(--border-subtle)', paddingBottom: 6 }}>
        Mise à disposition {restitue && <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>— restitué</span>}
      </h3>

      {!p?.verifieAvantUsage && rendu && !restitue && (
        <div style={{ background: 'var(--red-50)', borderInlineStart: '3px solid var(--red-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)' }}>
          <strong style={{ color: 'var(--red-600)' }}>Aptitude non vérifiée, et l’appareil est en service.</strong>{' '}
          Un appareil mis à disposition doit être vérifié apte <em>avant</em> de rendre le
          moindre résultat patient (ISO 15189 §6.4.3 et §7.3). Enregistrez la vérification,
          ou placez l’appareil hors service.
        </div>
      )}

      <div style={GRILLE}>
        <Rubrique label="Propriétaire">{p?.proprietaire}</Rubrique>
        <Rubrique label="N° de convention" mono>{p?.refConvention}</Rubrique>
        <Rubrique label="Remplace">
          {titulaire
            ? (
              <Bouton variante="primaire" taille="sm" onClick={() => { select('selectedEquip', titulaire.documentId); go('equip'); }} title={`Ouvrir la fiche ${titulaire.code}`} style={{ fontFamily: 'var(--font-mono)' }}>{titulaire.code} — {titulaire.nom} →</Bouton>
            )
            : p?.remplaceCode}
        </Rubrique>
        <Rubrique label="Motif" large>{p?.motif}</Rubrique>
        <Rubrique label="Mis à disposition le">{fmtDate(p?.dateMiseADispo) || p?.dateMiseADispo}</Rubrique>
        <Rubrique label="Restitution prévue">{fmtDate(p?.dateRestitutionPrevue) || p?.dateRestitutionPrevue}</Rubrique>
        <Rubrique label="Restitution effective">{fmtDate(p?.dateRestitution) || p?.dateRestitution}</Rubrique>
      </div>

      <div style={GRILLE}>
        <Rubrique label="Aptitude vérifiée avant usage">
          <span style={{ fontWeight: 700, color: p?.verifieAvantUsage ? 'var(--green-600)' : 'var(--red-600)' }}>
            {p?.verifieAvantUsage ? 'Oui' : 'Non'}
          </span>
        </Rubrique>
        <Rubrique label="Vérifiée le">{fmtDate(p?.dateVerification) || p?.dateVerification}</Rubrique>
        <Rubrique label="Par">{p?.verifiePar}</Rubrique>
        <Rubrique label="Enregistrement de la vérification" mono>{p?.refVerification}</Rubrique>
        <Rubrique label="Commentaire" large>{p?.commentaire}</Rubrique>
      </div>
    </section>
  );
}

/* ------------------------------------------- 1 bis. formulaire d'édition */

/** Champs texte de la fiche, dans l'ordre du gabarit papier — c'est celui que
 *  le laboratoire a sous les yeux quand il recopie. */
/** Rubriques du gabarit que N'A PAS un logiciel. Le formulaire les retire — il
 *  ne les grise pas : un champ grisé se lit « vous n'avez pas le droit », alors
 *  que la question ne se pose tout simplement pas. L'emplacement au plan et la
 *  périodicité de métrologie sont traités à part, hors de cette liste. */
const CHAMPS_SANS_OBJET_LOGICIEL = new Set<string>([
  'anneeFabrication', 'paysOrigine', 'localisation', 'accessoires',
  'voltage', 'amperage', 'puissance',
]);

const CHAMPS: { k: keyof GedEquipement; label: string; mono?: boolean; large?: boolean; requis?: boolean }[] = [
  // La désignation est la SEULE rubrique obligatoire, et c'est voulu : la fiche
  // reprend un document papier dont beaucoup de cases sont vides. Une rubrique
  // vide est un constat, pas un oubli — les exiger toutes ferait inventer des
  // valeurs pour pouvoir enregistrer.
  { k: 'nom', label: 'Désignation', requis: true },
  { k: 'marque', label: 'Marque' },
  { k: 'modele', label: 'Type / modèle' },
  { k: 'numeroSerie', label: 'N° de série', mono: true },
  { k: 'anneeFabrication', label: 'Année de fabrication' },
  { k: 'paysOrigine', label: 'Pays d’origine' },
  // `localisation` reste un champ LIBRE : c'est l'écrit de la fiche papier, et
  // le corriger au clavier fait partie de la reprise. L'emplacement structuré
  // (lot N5-2) n'est pas ici — il se choisit dans une liste, plus bas.
  { k: 'localisation', label: 'Localisation (fiche source)' },
  { k: 'accessoires', label: 'Accessoires', large: true },
  { k: 'voltage', label: 'Voltage' },
  { k: 'amperage', label: 'Ampérage' },
  { k: 'puissance', label: 'Puissance' },
  // `fournisseur` n'est plus un champ libre (28/10/2026) : il se choisit dans
  // l'annuaire, sous la grille (`fournisseurCode`).
  { k: 'dateReception', label: 'Date de réception' },
  { k: 'dateMiseEnService', label: 'Date de mise en service' },
  { k: 'garantieJusquA', label: 'Garantie jusqu’à' },
  { k: 'responsableNom', label: 'Responsable' },
  { k: 'responsablePoste', label: 'Poste du responsable' },
  { k: 'suppleantNom', label: 'Suppléant' },
  { k: 'suppleantPoste', label: 'Poste du suppléant' },
  { k: 'observation', label: 'Observation', large: true },
];

const METRO_VIDE: GedMetrologie = {
  suivi: '', raccordement: '', organisme: '', refAccreditation: '',
  refCertificat: '', dateCertificat: '', portee: '', emt: '', incertitude: '',
  constat: '', derniereOperation: '', verifIntermPeriodiciteMois: '', derniereVerifInterm: '', commentaire: '',
  // Lot ISO-5 — les maillons manquants de la chaîne de raccordement (§6.5).
  // `certificatPieceId` désigne LA PIÈCE qui porte le document : c'est ce que
  // l'évaluateur demande, et un numéro seul ne le lui donne pas.
  certificatPieceId: '', dateValiditeCertificat: '', etalonRef: '',
  rattachementNature: '', rattachementLibelle: '',
};

/** Les étalons du registre, pour désigner celui qui sert à un étalonnage
 *  INTERNE. Chargés ici plutôt que dans le magasin global : ils ne servent qu'à
 *  ce sélecteur, et les tirer à chaque rafraîchissement de la Qualité coûterait
 *  une lecture à tout le monde pour une poignée de fiches. */
function useEtalons() {
  const [etalons, setEtalons] = useState<{ ref: string; code: string; designation: string; statut: string }[]>([]);
  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged-etalons?pagination[pageSize]=100&sort=designation:asc', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((d) => { if (vivant) setEtalons(d?.data || []); })
      .catch(() => { if (vivant) setEtalons([]); });
    return () => { vivant = false; };
  }, []);
  return etalons;
}

const PRET_VIDE: GedPret = {
  proprietaire: '', refConvention: '', remplaceCode: '', motif: '',
  dateMiseADispo: '', dateRestitutionPrevue: '', dateRestitution: '',
  verifieAvantUsage: false, dateVerification: '', verifiePar: '', refVerification: '', commentaire: '',
};

/** Le contact technique tel que le formulaire le tient : société (référence + ancienne saisie) et techniciens. */
const contactInitial = (e: GedEquipement): ContactTech => ({
  societeCode: e.contactTechnique?.societeCode || '',
  societe: e.contactTechnique?.societe || '',
  techniciens: (e.contactTechnique?.techniciens || []).map((t) => ({ ...(t.code ? { code: t.code } : {}), nom: t.nom || '', tel: t.tel || '' })),
});

function FormulaireFiche({ e, onFerme }: { e: GedEquipement; onFerme: () => void }) {
  const { smq, settings, equips } = useGed();
  // On ne propose de remplacer QUE des appareils du parc : un prêt qui en
  // remplacerait un autre ne dirait rien de ce qui manque au laboratoire.
  const equipsDuParc = equips.filter((x) => x.code && !x.estPret && x.documentId !== e.documentId);
  const { run, estOccupe } = useBusy();
  const [f, setF] = useState<Record<string, string>>(() =>
    Object.fromEntries(CHAMPS.map((c) => [c.k, String(e[c.k] ?? '')])));
  const [periodicite, setPeriodicite] = useState(String(e.periodiciteMois ?? ''));
  /* ⚠️ Vrai par défaut : ce laboratoire TRAVAILLE les dimanches et les fériés.
     Le défaut inverse aurait déplacé des maintenances qui se font réellement. */
  const [nonOuvres, setNonOuvres] = useState(e.maintenanceJoursNonOuvres !== false);
  const [categorie, setCategorie] = useState(e.categorie || 'automate');
  /* ⚠️ LA FAMILLE COMMANDE LES CHAMPS OFFERTS (05/10/2026), et elle est
     COUPLÉE à la nature sur l'axe logiciel : choisir « Logiciel » ici pose
     aussi la nature, dans le même envoi. Sans cela le moteur refuserait — à
     juste titre — une fiche de famille Logiciel restée de nature `mesure`,
     c'est-à-dire autorisée à porter des cartes de contrôle interne.
     Les rubriques se recalculent donc à chaque changement de famille : on voit
     les sept rubriques sans objet disparaître au moment du choix, et non après
     l'enregistrement. */
  const estLog = categorie === CATEGORIE_LOGICIEL || natureEquip(e.nature).key === 'logiciel';
  const champsOfferts = React.useMemo(
    () => CHAMPS.filter((c) => !(estLog && CHAMPS_SANS_OBJET_LOGICIEL.has(String(c.k)))),
    [estLog],
  );
  const [discipline, setDiscipline] = useState(e.discipline || '');
  // Lot N5-2 — l'emplacement au plan du laboratoire.
  const [emplacementCode, setEmplacementCode] = useState(e.emplacementCode || '');
  const { proposables: emplacements, charge: emplacementsCharges } = useEmplacements();
  const [erreur, setErreur] = useState('');
  const [estPret, setEstPret] = useState(e.estPret === true);
  const [pret, setPret] = useState<GedPret>(() => ({ ...PRET_VIDE, ...(e.pret || {}) }));
  const [metro, setMetro] = useState<GedMetrologie>(() => ({ ...METRO_VIDE, ...(e.metrologie || {}) }));
  /* Contrat de maintenance et logiciel embarqué. Deux blocs qui s'AFFICHAIENT
     sans jamais pouvoir se saisir : le contrat venait de la reprise du classeur,
     et la version logicielle n'existait nulle part. */
  const [contrat, setContrat] = useState(() => ({ societe: '', societeCode: '', type: '', periode: '', fin: '', ...(e.contrat || {}) }));
  /* Fournisseur, société du contrat, contact technique : des fiches de
     l'annuaire (28/10/2026). Le moteur relit les noms et téléphones. */
  const [fournisseurCode, setFournisseurCode] = useState(e.fournisseurCode || '');
  const [contact, setContact] = useState<ContactTech>(() => contactInitial(e));
  const [logiciel, setLogiciel] = useState(() => ({ version: '', edition: '', releveLe: '', ...(e.logiciel || {}) }));
  /* ⚠️ CE QUE L'ÉCRAN A LU, FIGÉ À L'OUVERTURE (14/10/2026). Joint à l'envoi
     sous `base`, avec EXACTEMENT la forme du corps : le moteur n'écrit plus une
     rubrique que si elle diffère de cette lecture — et non plus de la base.
     Sans cela, la fiche entière renvoyée ici écrasait ce qu'un autre poste
     avait écrit pendant que le formulaire était ouvert (une vérification
     datée, un emplacement, l'inscription à la tournée de température).
     Figé par `useState` : un rafraîchissement du magasin pendant la saisie ne
     doit pas déplacer la référence, sinon la modification de la RQ se
     confondrait avec celle de l'autre poste. */
  const [base] = useState<Record<string, unknown>>(() => ({
    ...Object.fromEntries(CHAMPS.map((c) => [c.k, String(e[c.k] ?? '')])),
    categorie: e.categorie || 'automate', discipline: e.discipline || '', emplacementCode: e.emplacementCode || '',
    estPret: e.estPret === true,
    contrat: { societe: '', societeCode: '', type: '', periode: '', fin: '', ...(e.contrat || {}) },
    fournisseurCode: e.fournisseurCode || '',
    contactTechnique: contactInitial(e),
    logiciel: { version: '', edition: '', releveLe: '', ...(e.logiciel || {}) },
    pret: { ...PRET_VIDE, ...(e.pret || {}) },
    metrologie: { ...METRO_VIDE, ...(e.metrologie || {}) },
    ...(String(e.periodiciteMois ?? '').trim() ? { periodiciteMois: parseInt(String(e.periodiciteMois), 10) } : {}),
    maintenanceJoursNonOuvres: e.maintenanceJoursNonOuvres !== false,
  }));
  const etalons = useEtalons();
  // Seules les pièces de nature « certificat » peuvent porter le raccordement :
  // proposer un bon de livraison dans ce sélecteur reviendrait à laisser
  // documenter un étalonnage par n'importe quel PDF du dossier.
  const certificats = (e.pieces || []).filter((p) => p.nature === 'certificat');
  const discs = settings?.disciplines?.filter((d) => d.active) || [];

  /* Déclaration des champs DANS L'ORDRE D'AFFICHAGE : c'est cet ordre qui fait
     que le saut mène au premier problème en partant du haut. */
  const declaration = React.useMemo(() => [
    ...champsOfferts.map((c) => ({
      id: `eqf-${String(c.k)}`,
      label: c.label,
      valeur: f[c.k as keyof typeof f] as unknown,
      requis: !!c.requis,
    })),
    { id: 'eqf-cat', label: 'Catégorie', valeur: categorie, requis: true },
    { id: 'eqf-emp', label: 'Emplacement (plan du laboratoire)', valeur: emplacementCode },
    { id: 'eqf-disc', label: 'Discipline', valeur: discipline },
    /* La date de relevé n'est exigée QUE si une version est saisie. Le serveur
       la refuse de toute façon — mais un refus qui arrive après l'envoi fait
       remonter la page sans dire où regarder, alors que le socle marque le
       champ et y emmène. Les deux contrôles coexistent volontairement : celui
       du serveur tient aussi pour les scripts de reprise. */
    { id: 'eql-rel', label: 'Version logicielle relevée le', valeur: logiciel.releveLe, requis: !!logiciel.version.trim() },
  ], [f, champsOfferts, categorie, emplacementCode, discipline, logiciel]);
  const { ref: refFormulaire, verifier, Bandeau } = useFormulaire(declaration);

  const enregistrer = () => run('save', async () => {
    // Le contrôle passe par le socle partagé : il affiche le rappel en tête,
    // marque les champs fautifs et EMMÈNE au premier. L'ancien code posait un
    // message sous le bouton et rougissait la bordure — invisibles tous deux
    // quand on enregistre depuis le bas d'une fiche de trente rubriques.
    if (!verifier()) return;
    setErreur('');
    const corps: Record<string, unknown> = { ...f, categorie, discipline, emplacementCode, estPret, contrat, logiciel, fournisseurCode, contactTechnique: contact };
    /* ⚠️ LA NATURE VOYAGE AVEC LA FAMILLE (05/10/2026). Les deux sont couplées
       sur l'axe logiciel et le moteur refuse la contradiction : envoyer la
       famille seule ferait tomber un refus sur un enregistrement de fiche
       complète, et la saisie entière serait à reprendre. L'avertissement
       au-dessus du sélecteur a déjà dit ce que cela ferme. */
    if (categorie === CATEGORIE_LOGICIEL && natureEquip(e.nature).key !== 'logiciel') {
      corps.nature = 'logiciel';
    }
    if (estPret) corps.pret = pret;
    // Le bloc métrologique n'est envoyé que si la famille le réclame : le joindre
    // sur un automate écrirait un objet vide qui n'a rien à y faire.
    if (exigeMetrologie(categorie)) corps.metrologie = metro;
    if (periodicite.trim()) corps.periodiciteMois = parseInt(periodicite, 10);
    corps.maintenanceJoursNonOuvres = nonOuvres;
    corps.base = base;
    const ok = await smq(`ged/equip/${e.documentId}/fiche`, corps, 'Fiche mise à jour — modifications tracées au journal.');
    if (ok) onFerme();
  });

  return (
    <div ref={refFormulaire} style={{ display: 'grid', gap: 14 }}>
      <Bandeau />
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
        Chaque rubrique modifiée est inscrite au journal de l’équipement, avec sa valeur
        avant et après. Laissez vide ce que le document source ne renseigne pas :
        une rubrique vide est un constat, pas un oubli à combler au jugé —
        elle apparaît simplement en pointillés, pour se repérer d’un coup d’œil.
      </p>
      <div style={GRILLE}>
        {champsOfferts.map((c) => (
          <div key={String(c.k)} style={{ gridColumn: c.large ? '1 / -1' : undefined }}>
            <label style={lbl} htmlFor={`eqf-${String(c.k)}`}>
              {/* Mêmes champs, autre libellé sur un logiciel : « Éditeur » pour
                  `marque`, « N° de licence / clé » pour `numeroSerie`. Leur
                  donner des colonnes propres aurait dédoublé toutes les listes
                  et tous les exports du parc pour changer deux mots. */}
              {libelleRubrique(String(c.k), c.label, estLog)}
              {c.k === 'nom' && <span style={{ color: 'var(--red-600)' }}> *</span>}
            </label>
            <input id={`eqf-${String(c.k)}`} value={f[c.k as string]}
              onChange={(ev) => { setF({ ...f, [c.k]: ev.target.value }); if (erreur) setErreur(''); }}
              aria-invalid={c.k === 'nom' ? !!erreur : undefined}
              style={{ ...inputStyle, fontFamily: c.mono ? 'var(--font-mono)' : undefined, borderColor: c.k === 'nom' && erreur ? 'var(--red-600)' : undefined }} />
          </div>
        ))}
        <div>
          <label style={lbl} htmlFor="eqf-cat">Catégorie</label>
          <select id="eqf-cat" value={categorie} onChange={(ev) => setCategorie(ev.target.value)} style={inputStyle}>
            {EQUIP_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          {/* ⚠️ LE COUPLAGE EST ANNONCÉ, pas subi. La famille « Logiciel » pose
              aussi la NATURE logiciel — ce qui FERME les cartes de contrôle
              interne, les calibrations et le raccordement. C'est une
              conséquence, pas un détail de rangement : la taire ferait découvrir
              au prochain contrôle qu'un appareil n'en accepte plus. */}
          {categorie === CATEGORIE_LOGICIEL && natureEquip(e.nature).key !== 'logiciel' && (
            <div style={{ fontSize: 12.5, color: 'var(--amber-600)', marginTop: 4, lineHeight: 1.5 }}>
              Cette fiche passera aussi en <strong>nature LOGICIEL</strong> : ni carte de contrôle
              interne, ni calibration, ni raccordement métrologique ne pourront plus lui être
              rattachés. Les rubriques sans objet (alimentation, année, pays, salle) disparaissent.
            </div>
          )}
          {categorie !== CATEGORIE_LOGICIEL && natureEquip(e.nature).key === 'logiciel' && (
            <div style={{ fontSize: 12.5, color: 'var(--amber-600)', marginTop: 4, lineHeight: 1.5 }}>
              Cette fiche est de <strong>nature LOGICIEL</strong> : sa famille doit rester
              « Logiciel ». Pour en faire un appareil, changez d’abord sa nature dans le bloc en tête
              de l’onglet Identification.
            </div>
          )}
        </div>
        {/* ⚠️ UN LOGICIEL N'EST DANS AUCUNE PIÈCE : il tourne sur un ordinateur,
            ou en ligne depuis tous. Sa localisation se déclare dans « Où il
            tourne », et le moteur ne lui demande pas de salle. */}
        <div style={{ display: estLog ? 'none' : undefined }}>
          <label style={lbl} htmlFor="eqf-emp">Emplacement (plan du laboratoire)</label>
          <Selecteur id="eqf-emp" value={emplacementCode} onChange={setEmplacementCode} style={inputStyle}>
            {/* « Non renseigné » et non « — » : la case vide est un CONSTAT
                — personne n'a encore dit où cet appareil se trouve — et pas
                une valeur par défaut qu'on aurait choisie. */}
            <option value="">Non renseigné</option>
            {/* Un code déjà posé mais absent du référentiel reste proposé, sans
                quoi ouvrir le formulaire l'effacerait à l'enregistrement. */}
            {!!emplacementCode && !emplacements.some((x) => x.code === emplacementCode) && (
              <option value={emplacementCode}>{emplacementCode} (emplacement inconnu)</option>
            )}
            {emplacements.map((x) => <option key={x.code} value={x.code}>{x.chemin}</option>)}
          </Selecteur>
          {emplacementsCharges && !emplacements.length && (
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>
              Aucun emplacement n’est encore défini : le plan du laboratoire se crée dans le paramétrage.
            </div>
          )}
        </div>
        <div>
          <label style={lbl} htmlFor="eqf-disc">Discipline</label>
          <Selecteur id="eqf-disc" value={discipline} onChange={setDiscipline} style={inputStyle}>
            <option value="">—</option>
            {discs.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
          </Selecteur>
        </div>
        <div>
          <label style={lbl} htmlFor="eqf-per">{estLog ? 'Périodicité de maintenance (mois)' : 'Périodicité de métrologie (mois)'}</label>
          <input id="eqf-per" type="number" min={1} max={120} value={periodicite}
            onChange={(ev) => setPeriodicite(ev.target.value)} placeholder="non définie" style={inputStyle} />
        </div>
      </div>
      {/* ── FOURNISSEUR ET CONTACT TECHNIQUE, DANS L'ANNUAIRE (28/10/2026) ──
          Du texte libre jusqu'ici : la même société s'écrivait de quatre
          façons, et le téléphone du technicien vieillissait sur la fiche. */}
      <section style={{ display: 'grid', gap: 10, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12 }}>
        <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>Fournisseur et contact technique</h3>
        <div>
          <label style={lbl} htmlFor="eqf-fournisseur">Fournisseur</label>
          <ChoixCorrespondant id="eqf-fournisseur" valeur={fournisseurCode} onChange={setFournisseurCode}
            familles={FAMILLES_SOCIETE} vide="— aucun fournisseur"
            aide={!fournisseurCode && e.fournisseur
              ? <>Saisie ancienne : <strong>{e.fournisseur}</strong> — choisissez la fiche correspondante dans l’annuaire.</>
              : 'Les fournisseurs de l’annuaire. Absent ? Créez-le d’abord dans l’annuaire.'} />
        </div>
        <ChoixContactTechnique prefixe="eqct" valeur={contact} onChange={setContact} />
      </section>

      {/* Contrat de maintenance — il s'affichait sur la fiche sans qu'aucun
          écran ne permette de le saisir : les valeurs venaient toutes de la
          reprise du classeur. L'échéance est la seule rubrique qui dise si
          quelqu'un est encore TENU de venir réparer l'appareil. */}
      <section style={{ display: 'grid', gap: 10, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12 }}>
        <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>Contrat de maintenance</h3>
        <div style={GRILLE}>
          <div><label style={lbl} htmlFor="eqc-soc">Société</label>
            <ChoixCorrespondant id="eqc-soc" valeur={contrat.societeCode || ''} onChange={(v) => setContrat({ ...contrat, societeCode: v })}
              familles={FAMILLES_SOCIETE} vide="— aucune société"
              aide={!contrat.societeCode && contrat.societe
                ? <>Saisie ancienne : <strong>{contrat.societe}</strong> — choisissez la fiche dans l’annuaire.</>
                : 'Les fournisseurs de l’annuaire.'} /></div>
          <div><label style={lbl} htmlFor="eqc-typ">N° / formule</label>
            <input id="eqc-typ" value={contrat.type} onChange={(ev) => setContrat({ ...contrat, type: ev.target.value })} style={inputStyle} /></div>
          <div><label style={lbl} htmlFor="eqc-per">Périodicité contractuelle</label>
            <input id="eqc-per" value={contrat.periode} onChange={(ev) => setContrat({ ...contrat, periode: ev.target.value })} style={inputStyle} /></div>
          <div><label style={lbl} htmlFor="eqc-fin">Échéance du contrat</label>
            <input id="eqc-fin" type="date" value={contrat.fin} onChange={(ev) => setContrat({ ...contrat, fin: ev.target.value })} style={inputStyle} /></div>
        </div>
      </section>

      {/* Logiciel embarqué. Proposé sur TOUTES les fiches — un thermomètre n'en
          a pas, et la case restera vide, ce qui est un constat. Mais la fiche,
          elle, ne l'affiche que si quelque chose y a été écrit : c'est ici qu'on
          découvre la rubrique, pas sur la fiche d'un bain-marie. */}
      <section style={{ display: 'grid', gap: 10, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12 }}>
        <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          Logiciel embarqué <RefISO code="6.4.2" />
        </h3>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          La version est une rubrique d’identification, au même titre que le numéro de série :
          une vérification de méthode vaut pour la version sous laquelle elle a été conduite.
          Après une mise à jour, corrigez la version <em>et</em> redatez le relevé.
        </span>
        <div style={GRILLE}>
          <div><label style={lbl} htmlFor="eql-ver">Version installée</label>
            <input id="eql-ver" value={logiciel.version} onChange={(ev) => setLogiciel({ ...logiciel, version: ev.target.value })}
              placeholder="6.7.39" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
          <div><label style={lbl} htmlFor="eql-ed">Édition</label>
            <input id="eql-ed" value={logiciel.edition} onChange={(ev) => setLogiciel({ ...logiciel, edition: ev.target.value })}
              placeholder="SCA Human" style={inputStyle} /></div>
          <div><label style={lbl} htmlFor="eql-rel">Version relevée le</label>
            <input id="eql-rel" type="date" value={logiciel.releveLe} onChange={(ev) => setLogiciel({ ...logiciel, releveLe: ev.target.value })} style={inputStyle} /></div>
        </div>
      </section>

      {/* ⚠️ REMPLACE, AU NIVEAU DE L'APPAREIL, le réglage global « échéance
          tombant un jour non ouvré » que la RQ jugeait illisible le 25/08/2026.
          La question se pose bien appareil par appareil : un automate de garde
          tourne le dimanche, une centrifugeuse d'hormonologie non. */}
      <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 12.5, lineHeight: 1.55, cursor: 'pointer' }}>
        <input type="checkbox" checked={nonOuvres} onChange={(ev) => setNonOuvres(ev.target.checked)}
          style={{ accentColor: 'var(--color-primary)', marginTop: 2 }} />
        <span>
          <strong>Les maintenances de cet appareil ont lieu les jours non ouvrés</strong>
          <span style={{ display: 'block', color: 'var(--text-muted)' }}>
            {nonOuvres
              ? 'Dimanches et jours fériés compris : aucune échéance n’est déplacée, l’appareil est entretenu le jour où elle tombe.'
              : 'Une échéance tombant un dimanche ou un jour férié est déplacée, selon la politique de report réglée dans le paramétrage de la maintenance.'}
          </span>
        </span>
      </label>
      {/* Suivi métrologique. C'est la FAMILLE qui le commande, pas une case que
          l'utilisateur pourrait oublier de cocher : une pipette a besoin d'un
          raccordement, un automate non. */}
      {exigeMetrologie(categorie) && (
        <section style={{ display: 'grid', gap: 10, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12 }}>
          <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>
            Raccordement métrologique <RefISO code="6.5" />
          </h3>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Sans raccordement, l’instrument ne peut pas servir de critère de conformité.
            La périodicité d’étalonnage se règle plus haut, avec l’échéance.
          </span>
          <div style={GRILLE}>
            <div><label style={lbl} htmlFor="me-suivi">Nature du suivi</label>
              <select id="me-suivi" value={metro.suivi} onChange={(ev) => setMetro({ ...metro, suivi: ev.target.value })} style={inputStyle}>
                <option value="">—</option>
                <option value="etalonnage">Étalonnage</option>
                <option value="verification">Vérification</option>
                <option value="aucun">Aucun</option>
              </select></div>
            <div><label style={lbl} htmlFor="me-racc">Raccordement au SI</label>
              <select id="me-racc" value={metro.raccordement} onChange={(ev) => setMetro({ ...metro, raccordement: ev.target.value })} style={inputStyle}>
                <option value="">—</option>
                {Object.entries(RACCORDEMENTS).map(([k, v]) => <option key={k} value={k} title={v.aide}>{v.label}</option>)}
              </select></div>
            <div><label style={lbl} htmlFor="me-org">Organisme</label>
              <input id="me-org" value={metro.organisme} onChange={(ev) => setMetro({ ...metro, organisme: ev.target.value })} style={inputStyle} /></div>
            <div><label style={lbl} htmlFor="me-acc">N° d’accréditation</label>
              <input id="me-acc" value={metro.refAccreditation} onChange={(ev) => setMetro({ ...metro, refAccreditation: ev.target.value })} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
            <div><label style={lbl} htmlFor="me-cert">N° de certificat</label>
              <input id="me-cert" value={metro.refCertificat} onChange={(ev) => setMetro({ ...metro, refCertificat: ev.target.value })} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
            <div><label style={lbl} htmlFor="me-dcert">Date du certificat</label>
              <input id="me-dcert" type="date" value={metro.dateCertificat} onChange={(ev) => setMetro({ ...metro, dateCertificat: ev.target.value })} style={inputStyle} /></div>
            {/* ── Le certificat comme PIÈCE, et non comme numéro ─────────────
                C'est le constat d'audit qui a ouvert ce lot : le laboratoire
                saisissait un numéro, l'évaluateur demandait le document. Le
                sélecteur ne propose que les pièces déjà jointes à la fiche —
                on ne téléverse pas depuis ici, l'onglet « Documents » le
                fait déjà et deux chemins d'envoi divergeraient. */}
            <div><label style={lbl} htmlFor="me-cpj">Certificat joint (pièce)</label>
              <select id="me-cpj" value={metro.certificatPieceId || ''}
                onChange={(ev) => setMetro({ ...metro, certificatPieceId: ev.target.value })} style={inputStyle}>
                <option value="">— aucun document joint</option>
                {certificats.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name || p.libelle || p.reference}{p.dateValidite ? ` — jusqu’au ${p.dateValidite}` : ''}
                  </option>
                ))}
              </select>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                {certificats.length === 0
                  ? 'Aucun document de type « certificat » au dossier : joignez-le dans l’onglet Documents. Sans lui, le raccordement reste DÉCLARÉ, pas documenté.'
                  : 'Tant qu’aucun document n’est désigné, le raccordement est déclaré, jamais documenté.'}
              </span></div>
            <div><label style={lbl} htmlFor="me-dval">Certificat valable jusqu’au</label>
              <input id="me-dval" type="date" value={metro.dateValiditeCertificat || ''}
                onChange={(ev) => setMetro({ ...metro, dateValiditeCertificat: ev.target.value })} style={inputStyle} />
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                C’est cette date, et non celle d’émission, qui dit si le raccordement tient encore.
              </span></div>
            {/* Maillon 2 : l'étalon employé. Demandé UNIQUEMENT en étalonnage
                interne — quand un organisme accrédité mesure, il le fait avec
                ses propres étalons, et exiger ici un étalon du laboratoire
                ferait apparaître une chaîne rompue sur les instruments les
                mieux raccordés. */}
            {metro.raccordement === 'interne' && (
              <div><label style={lbl} htmlFor="me-eta">Étalon de référence employé <span style={{ color: 'var(--red-600)' }}>*</span></label>
                <select id="me-eta" value={metro.etalonRef || ''}
                  onChange={(ev) => setMetro({ ...metro, etalonRef: ev.target.value })} style={inputStyle}>
                  <option value="">— à désigner</option>
                  {etalons.map((x) => (
                    <option key={x.ref} value={x.code || x.ref}>
                      {x.code || x.ref} — {x.designation}{x.statut === 'retire' ? ' (retiré du service)' : ''}
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  {etalons.length === 0
                    ? 'Le registre des étalons est vide : une comparaison interne n’aurait aucune référence. Enregistrez l’étalon dans l’écran Métrologie.'
                    : 'La comparaison ne se raccorde qu’à travers cet étalon : c’est lui qui porte la suite de la chaîne.'}
                </span></div>
            )}
            {/* Maillon 4 : à quoi tout cela aboutit. En étalonnage interne il
                se lit sur l'étalon, pas ici — d'où le masquage. */}
            {metro.raccordement !== 'interne' && (
              <>
                <div><label style={lbl} htmlFor="me-ratn">Rattachement final</label>
                  <select id="me-ratn" value={metro.rattachementNature || ''}
                    onChange={(ev) => setMetro({ ...metro, rattachementNature: ev.target.value })} style={inputStyle}>
                    <option value="">—</option>
                    {Object.entries(RATTACHEMENTS_METRO).map(([k, v]) => <option key={k} value={k} title={v.aide}>{v.label}</option>)}
                  </select></div>
                <div><label style={lbl} htmlFor="me-ratl">Nommez la référence</label>
                  <input id="me-ratl" value={metro.rattachementLibelle || ''}
                    onChange={(ev) => setMetro({ ...metro, rattachementLibelle: ev.target.value })}
                    placeholder="LNE · ILAC MRA · PTB…" style={inputStyle} /></div>
              </>
            )}
            <div><label style={lbl} htmlFor="me-portee">Étendue de mesure</label>
              <input id="me-portee" value={metro.portee} onChange={(ev) => setMetro({ ...metro, portee: ev.target.value })} placeholder="100–1000 µL · 2–8 °C" style={inputStyle} /></div>
            <div><label style={lbl} htmlFor="me-emt">Erreur maximale tolérée</label>
              <input id="me-emt" value={metro.emt} onChange={(ev) => setMetro({ ...metro, emt: ev.target.value })} placeholder="± 0,8 %" style={inputStyle} /></div>
            <div><label style={lbl} htmlFor="me-inc">Incertitude</label>
              <input id="me-inc" value={metro.incertitude} onChange={(ev) => setMetro({ ...metro, incertitude: ev.target.value })} style={inputStyle} /></div>
            <div><label style={lbl} htmlFor="me-cons">Dernier constat</label>
              <select id="me-cons" value={metro.constat} onChange={(ev) => setMetro({ ...metro, constat: ev.target.value })} style={inputStyle}>
                <option value="">—</option>
                {Object.entries(CONSTATS_METRO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select></div>
            <div><label style={lbl} htmlFor="me-dop">Dernière opération</label>
              <input id="me-dop" type="date" value={metro.derniereOperation} onChange={(ev) => setMetro({ ...metro, derniereOperation: ev.target.value })} style={inputStyle} /></div>
            <div><label style={lbl} htmlFor="me-vip">Vérification intermédiaire (mois)</label>
              <input id="me-vip" type="number" min={1} max={120} value={metro.verifIntermPeriodiciteMois}
                onChange={(ev) => setMetro({ ...metro, verifIntermPeriodiciteMois: ev.target.value })} style={inputStyle} /></div>
            <div><label style={lbl} htmlFor="me-dvi">Dernière vérification intermédiaire</label>
              <input id="me-dvi" type="date" value={metro.derniereVerifInterm} onChange={(ev) => setMetro({ ...metro, derniereVerifInterm: ev.target.value })} style={inputStyle} /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="me-com">Commentaire</label>
              <input id="me-com" value={metro.commentaire} onChange={(ev) => setMetro({ ...metro, commentaire: ev.target.value })} style={inputStyle} /></div>
          </div>
        </section>
      )}

      {/* Régime de propriété. La case commande l'affichage du reste : tant que
          l'appareil est au parc, ces rubriques n'ont pas d'objet et encombrent. */}
      <section style={{ display: 'grid', gap: 10, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--text-strong)', cursor: 'pointer' }}>
          <input type="checkbox" checked={estPret} onChange={(ev) => setEstPret(ev.target.checked)}
            style={{ accentColor: 'var(--color-primary)' }} />
          Appareil mis à disposition par un tiers (prêt, dépannage, évaluation)
        </label>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Cochez si l’appareil n’appartient pas au laboratoire. Il rend malgré tout des
          résultats sous son propre numéro de série : il lui faut donc une identification,
          une vérification d’aptitude avant usage, et une date de restitution.
        </span>

        {estPret && (
          <>
            <div style={GRILLE}>
              <div><label style={lbl} htmlFor="pr-prop">Propriétaire <span style={{ color: 'var(--red-600)' }}>*</span></label>
                <input id="pr-prop" value={pret.proprietaire} onChange={(ev) => setPret({ ...pret, proprietaire: ev.target.value })}
                  placeholder="Société qui met l’appareil à disposition" style={inputStyle} /></div>
              <div><label style={lbl} htmlFor="pr-conv">N° de convention / bon</label>
                <input id="pr-conv" value={pret.refConvention} onChange={(ev) => setPret({ ...pret, refConvention: ev.target.value })}
                  style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
              <div><label style={lbl} htmlFor="pr-remp">Remplace l’équipement</label>
                <Selecteur id="pr-remp" value={pret.remplaceCode} onChange={(v) => setPret({ ...pret, remplaceCode: v })} style={inputStyle}>
                  <option value="">— aucun</option>
                  {equipsDuParc.map((x) => <option key={x.code} value={x.code}>{x.code} — {x.nom}</option>)}
                </Selecteur></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="pr-motif">Motif</label>
                <input id="pr-motif" value={pret.motif} onChange={(ev) => setPret({ ...pret, motif: ev.target.value })}
                  placeholder="Ex. réparation de l’appareil titulaire" style={inputStyle} /></div>
              <div><label style={lbl} htmlFor="pr-d1">Mis à disposition le <span style={{ color: 'var(--red-600)' }}>*</span></label>
                <input id="pr-d1" type="date" value={pret.dateMiseADispo} onChange={(ev) => setPret({ ...pret, dateMiseADispo: ev.target.value })} style={inputStyle} /></div>
              <div><label style={lbl} htmlFor="pr-d2">Restitution prévue</label>
                <input id="pr-d2" type="date" value={pret.dateRestitutionPrevue} onChange={(ev) => setPret({ ...pret, dateRestitutionPrevue: ev.target.value })} style={inputStyle} /></div>
              <div><label style={lbl} htmlFor="pr-d3">Restitution effective</label>
                <input id="pr-d3" type="date" value={pret.dateRestitution} onChange={(ev) => setPret({ ...pret, dateRestitution: ev.target.value })} style={inputStyle} /></div>
            </div>

            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', cursor: 'pointer' }}>
              <input type="checkbox" checked={pret.verifieAvantUsage}
                onChange={(ev) => setPret({ ...pret, verifieAvantUsage: ev.target.checked })}
                style={{ accentColor: 'var(--color-primary)' }} />
              Aptitude vérifiée AVANT la mise en service
            </label>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              Exigé par l’ISO 15189 §6.4.3 et §7.3 : contrôles internes passés, comparaison
              avec l’appareil remplacé. Ne cochez que si l’enregistrement existe.
            </span>
            <div style={GRILLE}>
              <div><label style={lbl} htmlFor="pr-vd">Vérifiée le</label>
                <input id="pr-vd" type="date" value={pret.dateVerification} onChange={(ev) => setPret({ ...pret, dateVerification: ev.target.value })} style={inputStyle} /></div>
              <div><label style={lbl} htmlFor="pr-vp">Par</label>
                <input id="pr-vp" value={pret.verifiePar} onChange={(ev) => setPret({ ...pret, verifiePar: ev.target.value })} style={inputStyle} /></div>
              <div><label style={lbl} htmlFor="pr-vr">Enregistrement</label>
                <input id="pr-vr" value={pret.refVerification} onChange={(ev) => setPret({ ...pret, refVerification: ev.target.value })}
                  placeholder="Référence du CQI, du rapport de comparaison…" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="pr-com">Commentaire</label>
                <input id="pr-com" value={pret.commentaire} onChange={(ev) => setPret({ ...pret, commentaire: ev.target.value })} style={inputStyle} /></div>
            </div>
          </>
        )}
      </section>

      {erreur && <p style={{ margin: 0, fontSize: 12.5, color: 'var(--red-600)', fontWeight: 600 }}>{erreur}</p>}
      <PiedActions taille="sm" annuler={{ onClick: onFerme }} principale={{ libelle: 'Enregistrer la fiche', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
    </div>
  );
}

/* ------------------------------------------------------ 2. fiche de vie */

export function FicheDeVie({ e, onVersMaintenance, onVersCalibrations }: { e: GedEquipement; onVersMaintenance?: () => void; onVersCalibrations?: () => void }) {
  const { smq, session, impactSortieEquip } = useGed();
  const { run, estOccupe } = useBusy();
  const peutSaisir = session.roles.length > 0;

  // Aperçu SEUL (aucune écriture) de ce qu'une panne annoncerait sur les
  // techniques et les examens — même calcul que la sortie de service
  // (`GET /ged/equip/:id/impact-sortie`), demandé par la RQ le 13/09/2026.
  // Une panne ne désactive rien d'elle-même (voir le commentaire de
  // `addPanne` côté serveur) : ce bandeau est là pour INFORMER avant que le
  // constat soit déclaré, pas pour bloquer la déclaration.
  const [impactPanne, setImpactPanne] = useState<ImpactSortieEquip | null>(null);
  useEffect(() => {
    let vivant = true;
    if (peutSaisir) impactSortieEquip(e.documentId).then((r) => { if (vivant) setImpactPanne(r); });
    return () => { vivant = false; };
  }, [e.documentId, peutSaisir, impactSortieEquip]);

  const [np, setNp] = useState({ date: '', cause: '', causeTexte: '', observation: '', gravite: 'majeure', dureeArret: '' });
  const [erreurPanne, setErreurPanne] = useState('');

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      {/* LA FRISE DE L'APPAREIL (Ines, 29/10/2026) : acquisition en tête, puis
          toutes ses NC, maintenances, métrologie et changements d'état, avec
          les indicateurs. Elle remplace la liste des seules pannes. */}
      <VieEquipement e={e} onVersMaintenance={onVersMaintenance} onVersCalibrations={onVersCalibrations} />

      {/* ---- pannes = non-conformités ---- */}
      <Bloc titre="Déclarer une panne ou une anomalie">
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
          Une panne est une <strong>non-conformité</strong> : elle prend une référence au registre
          qualité et en suit le circuit — analyse de cause, action corrective, clôture. Elle apparaît
          aussitôt dans la frise ci-dessus.
        </p>
        {peutSaisir && (
          <div style={{ display: 'grid', gap: 8, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <div>
                <label style={lbl} htmlFor="pa-date">Date</label>
                <input id="pa-date" type="date" value={np.date} onChange={(ev) => setNp({ ...np, date: ev.target.value })}
                  style={{ ...inputStyle, width: 'auto' }} />
              </div>
              <div style={{ minWidth: 230 }}>
                <label style={lbl} htmlFor="pa-cause">Code cause (registre de maintenance)</label>
                <select id="pa-cause" value={np.cause} onChange={(ev) => setNp({ ...np, cause: ev.target.value })} style={inputStyle}>
                  <option value="">— non déterminée</option>
                  {Object.entries(CAUSES_PANNE).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
                </select>
              </div>
              <div style={{ minWidth: 150 }}>
                <label style={lbl} htmlFor="pa-grav">Gravité</label>
                <select id="pa-grav" value={np.gravite} onChange={(ev) => setNp({ ...np, gravite: ev.target.value })} style={inputStyle}>
                  {NC_GRAVITES.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
                </select>
              </div>
              <div style={{ minWidth: 140 }}>
                <label style={lbl} htmlFor="pa-duree">Durée d’arrêt</label>
                <input id="pa-duree" value={np.dureeArret} onChange={(ev) => setNp({ ...np, dureeArret: ev.target.value })}
                  placeholder="2 h, 3 jours…" style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={lbl} htmlFor="pa-obs">Constat <span style={{ color: 'var(--red-600)' }}>*</span></label>
              <input id="pa-obs" value={np.observation}
                onChange={(ev) => { setNp({ ...np, observation: ev.target.value }); if (erreurPanne) setErreurPanne(''); }}
                placeholder="Ce qui a été observé au poste"
                aria-invalid={!!erreurPanne}
                style={{ ...inputStyle, borderColor: erreurPanne ? 'var(--red-600)' : undefined }} />
              {erreurPanne && <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--red-600)', fontWeight: 600 }}>{erreurPanne}</p>}
            </div>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              Le code cause dit d’où vient la panne ; l’analyse de cause, elle, se fait dans le dossier de la non-conformité.
            </span>
            {!!impactPanne?.techniquesADesactiver.length && (
              <div style={{ display: 'grid', gap: 4, background: 'var(--amber-50)', border: '1px solid var(--amber-600)', borderRadius: 'var(--radius-md)', padding: 10 }}>
                <span style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, fontWeight: 600, color: 'var(--amber-600)' }}>
                  <I n="alert" s={14} />
                  {`Cet appareil est le seul recours de ${impactPanne.techniquesADesactiver.length} technique(s) : ${impactPanne.techniquesADesactiver.map((t) => t.code).join(', ')}.`}
                </span>
                {!!impactPanne.examensSansTechnique.length && (
                  <span style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>
                    {`Si la panne se confirme durable, ${impactPanne.examensSansTechnique.length} examen(s) resteront SANS AUCUNE technique active : `}
                    {impactPanne.examensSansTechnique.map((f) => `${f.code} — ${f.libelle}`).join(' · ')}
                  </span>
                )}
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  Déclarer la panne n’écrit rien sur les techniques ; c’est la sortie du service de l’appareil qui les désactivera.
                </span>
              </div>
            )}
            <Bouton variante="primaire" taille="sm" onClick={() => void run('panne', async () => {
              if (!np.observation.trim()) { setErreurPanne('Décrivez la panne constatée.'); return; }
              setErreurPanne('');
              const ok = await smq(`ged/equip/${e.documentId}/panne`, np,
                'Panne déclarée — une non-conformité a été ouverte au registre.');
              if (ok) setNp({ date: '', cause: '', causeTexte: '', observation: '', gravite: 'majeure', dureeArret: '' });
            })} disabled={estOccupe()} style={{ width: 'fit-content' }}>
              <I n="plus" s={13} />{estOccupe('panne') ? 'Enregistrement…' : 'Déclarer la panne'}
            </Bouton>
          </div>
        )}
      </Bloc>

      {/* ---- les maintenances ont quitté cet onglet (29/10/2026) ----
          « Un seul onglet pour toutes les maintenances, et on choisit son
          type » (RQ). Préventives du plan, hors plan, curatives : tout se
          saisit et se relit dans l'onglet Maintenance, par la même porte. */}
      <Bloc titre="Enregistrer une maintenance">
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
          Les maintenances réalisées — du plan, hors plan, curatives, étalonnages — figurent dans la frise
          ci-dessus. Elles se saisissent dans l’onglet <strong>Maintenance</strong>, bouton « Saisir une
          maintenance » ; c’est aussi là qu’on programme, modifie ou retire les maintenances du plan.
        </p>
        {onVersMaintenance && (
          <Bouton taille="sm" onClick={onVersMaintenance} style={{ width: 'fit-content' }}>
            Ouvrir l’onglet Maintenance
          </Bouton>
        )}
      </Bloc>
    </div>
  );
}

/**
 * Correction d'une intervention DÉJÀ TRACÉE, même ancienne (N2-1).
 *
 * Ce qui rend la chose acceptable dans un système accrédité n'est pas de
 * l'interdire, c'est de la TRACER : le serveur journalise chaque rubrique
 * modifiée avec son ancienne valeur en regard, au journal chaîné de l'appareil
 * — le mécanisme de la fiche signalétique (lot C8), étendu et non réinventé.
 *
 * La ligne garde son identifiant : le rapport du prestataire déjà joint reste
 * attaché à l'intervention qu'il prouve. C'est aussi pourquoi ce formulaire ne
 * touche ni au fichier, ni à l'auteur de la saisie d'origine.
 */
export function CorrigerIntervention({ iv, equipId, onFerme }: {
  iv: GedIntervention; equipId: string; onFerme: () => void;
}) {
  const { smq, equips } = useGed();
  /* Mêmes listes qu'à la saisie (RQ, 03/11/2026) : une correction ne doit pas
     réintroduire le texte libre que la saisie a retiré. */
  const appareil = equips.find((x) => x.documentId === equipId) ?? null;
  const habilites = useHabilites(appareil?.documentId);
  const piecesAppareil = piecesConnues((appareil?.interventions ?? []).filter((x) => x.id !== iv.id).map((x) => x.piecesRechange));
  const { run, estOccupe } = useBusy();
  const [f, setF] = useState({
    date: iv.date || '', intervType: iv.intervType || 'maintenance', nature: iv.nature || '',
    prestataire: iv.prestataire || '', operateur: iv.operateur || '', commentaire: iv.commentaire || '',
    dureeArret: iv.dureeArret || '', piecesRechange: iv.piecesRechange || '',
    codeFiche: iv.codeFiche || '', numFacture: iv.numFacture || '',
    remisEnServiceLe: iv.remisEnServiceLe || '', remisEnServicePar: iv.remisEnServicePar || '',
    remisEnServiceVerif: iv.remisEnServiceVerif || '', modalite: iv.modalite || '',
    // `null` ≠ `false` : une ligne reprise du registre papier ne porte aucun
    // verdict, et le formulaire doit pouvoir la laisser telle quelle.
    conforme: iv.conforme,
  });

  return (
    <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '12px 0', display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>Corriger l’intervention du {fmtDate(iv.date) || iv.date || '—'}</strong>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Chaque rubrique modifiée part au journal de l’appareil avec son ancienne valeur en regard.
        </span>
      </div>
      <div style={GRILLE}>
        <div><label style={lbl} htmlFor={`ci-date-${iv.id}`}>Date</label>
          <input id={`ci-date-${iv.id}`} type="date" value={f.date} onChange={(ev) => setF({ ...f, date: ev.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor={`ci-type-${iv.id}`}>Nature</label>
          <select id={`ci-type-${iv.id}`} value={f.intervType} onChange={(ev) => setF({ ...f, intervType: ev.target.value })} style={inputStyle}>
            {INTERV_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select></div>
        <div><label style={lbl} htmlFor={`ci-nat-${iv.id}`}>Type</label>
          <select id={`ci-nat-${iv.id}`} value={f.nature} onChange={(ev) => setF({ ...f, nature: ev.target.value })} style={inputStyle}>
            <option value="">— non précisé</option>
            <option value="preventive">Préventive</option>
            <option value="curative">Curative</option>
          </select></div>
        <div><label style={lbl} htmlFor={`ci-mod-${iv.id}`}>Modalité</label>
          <select id={`ci-mod-${iv.id}`} value={f.modalite} onChange={(ev) => setF({ ...f, modalite: ev.target.value as typeof f.modalite })} style={inputStyle}>
            <option value="">— non précisée</option>
            <option value="sur_site">Sur site</option>
            <option value="telemaintenance">Télémaintenance (à distance)</option>
          </select></div>
        <div><label style={lbl} htmlFor={`ci-pres-${iv.id}`}>Prestataire</label>
          <ChoixListe id={`ci-pres-${iv.id}`} valeur={f.prestataire} onChange={(v) => setF({ ...f, prestataire: v })}
            vide="Aucun — fait en interne" groupes={[{ titre: 'Sociétés de la fiche de l’appareil', options: societesDe(appareil) }]} /></div>
        <div><label style={lbl} htmlFor={`ci-ope-${iv.id}`}>Opérateur</label>
          <ChoixListe id={`ci-ope-${iv.id}`} valeur={f.operateur} onChange={(v) => setF({ ...f, operateur: v })} vide="— choisir —"
            groupes={[
              { titre: 'Personnes habilitées sur l’appareil', options: optionsHabilites(habilites) },
              { titre: 'Techniciens du service technique', options: techniciensPrestataire(appareil) },
            ]} />
          {etatHabilites(habilites) && <div style={AIDE_CHAMP}>{etatHabilites(habilites)}</div>}</div>
        <div><label style={lbl} htmlFor={`ci-dur-${iv.id}`}>Durée d’arrêt</label>
          <ChampDuree id={`ci-dur-${iv.id}`} valeur={f.dureeArret} onChange={(v) => setF((x) => ({ ...x, dureeArret: v }))} /></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor={`ci-pie-${iv.id}`}>Pièces de rechange</label>
          <ChampPieces id={`ci-pie-${iv.id}`} valeur={f.piecesRechange} onChange={(v) => setF((x) => ({ ...x, piecesRechange: v }))} connues={piecesAppareil} /></div>
        <div><label style={lbl} htmlFor={`ci-cod-${iv.id}`}>Code fiche prestataire</label>
          <input id={`ci-cod-${iv.id}`} value={f.codeFiche} onChange={(ev) => setF({ ...f, codeFiche: ev.target.value })} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
        <div><label style={lbl} htmlFor={`ci-fac-${iv.id}`}>N° de facture</label>
          <input id={`ci-fac-${iv.id}`} value={f.numFacture} onChange={(ev) => setF({ ...f, numFacture: ev.target.value })} style={inputStyle} /></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor={`ci-com-${iv.id}`}>Description de l’intervention</label>
          <input id={`ci-com-${iv.id}`} value={f.commentaire} onChange={(ev) => setF({ ...f, commentaire: ev.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor={`ci-rsl-${iv.id}`}>Remise en service — date</label>
          <input id={`ci-rsl-${iv.id}`} type="date" min={f.date || undefined} value={f.remisEnServiceLe} onChange={(ev) => setF({ ...f, remisEnServiceLe: ev.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor={`ci-rsp-${iv.id}`}>Remise en service — par</label>
          <ChoixListe id={`ci-rsp-${iv.id}`} valeur={f.remisEnServicePar} onChange={(v) => setF({ ...f, remisEnServicePar: v })} vide="— choisir —"
            groupes={[{ titre: 'Personnes habilitées sur l’appareil', options: optionsHabilites(habilites) }]} /></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor={`ci-rsv-${iv.id}`}>Vérification faite avant la remise en service</label>
          <ChampVerification id={`ci-rsv-${iv.id}`} valeur={f.remisEnServiceVerif} onChange={(v) => setF((x) => ({ ...x, remisEnServiceVerif: v }))} /></div>
      </div>
      <div>
        <label style={lbl} htmlFor={`ci-conf-${iv.id}`}>Conformité après intervention</label>
        <select id={`ci-conf-${iv.id}`} value={f.conforme === null || f.conforme === undefined ? '' : f.conforme ? 'oui' : 'non'}
          onChange={(ev) => setF({ ...f, conforme: ev.target.value === '' ? null : ev.target.value === 'oui' })}
          style={{ ...inputStyle, width: 'auto', minWidth: 220 }}>
          <option value="">Non renseignée (ligne reprise du papier)</option>
          <option value="oui">Conforme</option>
          <option value="non">Non conforme</option>
        </select>
      </div>
      <PiedActions taille="sm" annuler={{ onClick: onFerme }} principale={{ libelle: 'Enregistrer la correction', libelleChargement: 'Enregistrement…', chargement: estOccupe('corr'), disabled: estOccupe(), onClick: () => void run('corr', async () => {
          // `conforme` n'est envoyé que s'il porte une décision : le serveur
          // n'accepte qu'un booléen, et laisser « non renseignée » doit
          // vouloir dire « ne touche pas », pas « écris false ».
          const corps: Record<string, unknown> = { ...f };
          if (f.conforme === null || f.conforme === undefined) delete corps.conforme;
          const ok = await smq(`ged/equip/${equipId}/intervention/${iv.id}`, corps,
            'Intervention corrigée — modification tracée au journal.');
          if (ok) onFerme();
        }) }} />
    </div>
  );
}

/* ------------------------------------- 3. documents (ex-« pièces jointes ») */

const NATURES: { k: string; label: string }[] = [
  { k: 'notice', label: 'Notice / manuel d’utilisation' },
  { k: 'installation', label: 'PV d’installation' },
  { k: 'certificat', label: 'Certificat / raccordement' },
  { k: 'miseajour', label: 'Mise à jour logicielle' },
  { k: 'rapport', label: 'Rapport d’intervention' },
  { k: 'facture', label: 'Facture' },
  { k: 'preuve', label: 'Pièce justificative' },
  // Un LIEN seulement, jamais un fichier : la vidéo reste chez son éditeur et
  // s'enregistre comme une référence externe, tracée au journal (03/11/2026).
  { k: 'video', label: 'Vidéo de démonstration' },
  { k: 'autre', label: 'Autre' },
];

const METAVIDE = { reference: '', version: '', url: '', dateDocument: '' };

/** Les rubriques exigées pour VERSER UN FICHIER, arbitrage du 10/09/2026.
 *
 *  La référence n'en est pas, sciemment : un PV d'installation ou une facture
 *  manuscrite scannée n'en porte souvent aucune, et l'exiger ferait inventer un
 *  numéro. Ces trois-là existent sur tout document.
 *
 *  ⚠️ Le serveur tient LA MÊME liste (`EXIGEES_FICHIER`) et refuse de son côté.
 *  Les deux contrôles coexistent volontairement : celui-ci marque le champ et y
 *  emmène, celui du serveur vaut aussi pour les scripts de reprise. */
const EXIGEES_FICHIER = [
  { id: 'pj-nature', label: 'Type de document' },
  { id: 'pj-date', label: 'Date du document' },
  { id: 'pj-lib', label: 'Libellé' },
];

/** Ce que le fichier a déclaré, dit en une phrase.
 *
 *  Un formulaire pré-rempli et un formulaire vide se ressemblent : sans cette
 *  phrase, personne ne sait s'il relit une donnée LUE DANS LE DOCUMENT ou s'il
 *  en invente une. C'est la raison d'être du bandeau — pas la décoration. */
function BandeauMetadonnees({ a }: { a: PieceAnalysee }) {
  const lus = [a.trouve.includes('titre') && 'le titre', a.trouve.includes('dateDocument') && 'la date']
    .filter(Boolean) as string[];
  const chaud = lus.length > 0;
  return (
    <div style={{
      background: chaud ? 'var(--green-50)' : 'var(--amber-50)',
      borderInlineStart: `3px solid ${chaud ? 'var(--green-600)' : 'var(--amber-600)'}`,
      borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, lineHeight: 1.55,
      display: 'grid', gap: 6,
    }}>
      <span>
        {chaud
          ? <><strong style={{ color: 'var(--text-strong)' }}>Lu dans le fichier : {lus.join(' et ')}.</strong>{' '}
            Vérifiez, corrigez si besoin, et complétez ce qui manque.</>
          : <><strong style={{ color: 'var(--text-strong)' }}>{a.empeche || 'Ce fichier ne déclare ni titre ni date.'}</strong>{' '}
            Tout est donc à saisir : les rubriques ci-dessous nomment le document, sans elles la pièce est un fichier anonyme dans un dossier.</>}
      </span>
      {/* Le bloc BRUT. Un PDF n'a pas de case « référence » : elle vit dans le
          titre ou les mots clés, sous une forme que seul un humain reconnaît.
          On la montre à recopier plutôt que de l'extraire par ressemblance. */}
      {a.declare.length > 0 && (
        <div style={{ display: 'grid', gap: 2, color: 'var(--text-muted)' }}>
          {a.declare.map((d) => (
            <span key={d.cle}><span style={{ fontWeight: 600 }}>{d.cle}</span> : {d.valeur}</span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * LES DOCUMENTS INTERNES de l'appareil — procédures, modes opératoires,
 * enregistrements de la GED (Ines, 29/10/2026 : « permettre d'y lier des
 * documents internes également »).
 *
 * Aucun stockage nouveau : c'est le registre des LIENS, dans les deux sens
 * (appareil → document, ou document → appareil), celui qu'utilise déjà
 * l'onglet « Liens & NC ». Un document s'ouvre en panneau, la fiche reste dessous.
 */
function DocumentsInternes({ e }: { e: GedEquipement }) {
  const { docs, liens, creerLien, retirerLien, session, ouvrirPanneau } = useGed();
  const code = e.code || '';
  const [choix, setChoix] = useState('');
  const [enCours, setEnCours] = useState(false);
  const estRQ = session.roles.includes('rq');
  const lies = liens
    .filter((l) => (l.sourceType === 'equipement' && l.sourceRef === code && l.cibleType === 'document')
      || (l.cibleType === 'equipement' && l.cibleRef === code && l.sourceType === 'document'))
    .map((l) => ({ lien: l, ref: l.sourceType === 'document' ? l.sourceRef : l.cibleRef }));
  const dejaLies = new Set(lies.map((x) => x.ref));
  const proposables = docs.filter((d) => d.code && !dejaLies.has(d.code) && d.statut !== 'retire')
    .sort((a, b) => String(a.code).localeCompare(String(b.code), 'fr'));
  if (!code) return null;
  return (
    <Bloc titre={`Documents internes (${lies.length})`}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
        Procédures, modes opératoires et enregistrements de la gestion documentaire qui concernent cet appareil.
      </p>
      {lies.map(({ lien, ref }) => {
        const d = docs.find((x) => x.code === ref);
        return (
          <div key={lien.documentId} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5 }}>
            <Bouton taille="sm" variante="discret" onClick={() => ouvrirPanneau({ type: 'doc', valeur: ref, origine: code })}
              style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{ref}</Bouton>
            <span style={{ flex: 1, color: 'var(--text-body)' }}>{d?.titre || 'document introuvable au catalogue'}</span>
            {d?.versionCourante && <span style={{ color: 'var(--text-muted)' }}>v{d.versionCourante}</span>}
            {estRQ && (
              <ConfirmButton label="" titre="Retirer ce lien" icon="x" variant="outline"
                question={`Ne plus lier ${ref} à cet appareil ? Le document lui-même n’est pas touché.`} confirmer="Retirer"
                onConfirm={async () => { await retirerLien(lien.documentId); }} />
            )}
          </div>
        );
      })}
      {session.roles.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Selecteur id={`doc-int-${e.documentId}`} value={choix} onChange={setChoix} style={{ ...inputStyle, maxWidth: 440 }}>
            <option value="">Lier un document de la GED…</option>
            {proposables.map((d) => <option key={d.code} value={d.code}>{d.code} — {d.titre}</option>)}
          </Selecteur>
          <Bouton taille="sm" variante="primaire" disabled={!choix || enCours} onClick={() => {
            setEnCours(true);
            void creerLien({ sourceType: 'equipement', sourceRef: code, cibleType: 'document', cibleRef: choix, typeLien: 'concerne' })
              .then((ok) => { if (ok) setChoix(''); })
              .finally(() => setEnCours(false));
          }}>
            <I n="plus" s={12} /> Lier
          </Bouton>
        </div>
      )}
    </Bloc>
  );
}

export function Pieces({ e }: { e: GedEquipement }) {
  const { session, joindreFichierEquip, enregistrerReference, retirerPieceEquip, analyserPieceEquip } = useGed();
  const [nature, setNature] = useState('notice');
  const [libelle, setLibelle] = useState('');
  const [meta, setMeta] = useState(METAVIDE);
  const [envoi, setEnvoi] = useState(false);
  /* Le fichier CHOISI mais pas encore versé, et ce qu'il déclare. Deux gestes
     et non un : on lit d'abord, on complète, on verse ensuite. Verser puis
     corriger aurait laissé au dossier des pièces mal nommées le temps que
     quelqu'un y revienne — c'est-à-dire pour toujours. */
  const [enAttente, setEnAttente] = useState<File | null>(null);
  const [analyse, setAnalyse] = useState<PieceAnalysee | null>(null);
  const manquantes = enAttente
    ? EXIGEES_FICHIER.filter((c) => !(c.id === 'pj-nature' ? nature : c.id === 'pj-date' ? meta.dateDocument : libelle).trim())
    : [];
  const raz = () => { setLibelle(''); setMeta(METAVIDE); setEnAttente(null); setAnalyse(null); };
  const peutParc = peutGererParc(session.roles);
  const peutJoindre = session.roles.length > 0;
  const pieces = (e.pieces || []).slice().sort((a, b) => (b.ajouteLe || '').localeCompare(a.ajouteLe || ''));
  const parNature = useMemo(() => {
    const m = new Map<string, GedPieceEquip[]>();
    for (const p of pieces) m.set(p.nature, [...(m.get(p.nature) || []), p]);
    return m;
  }, [pieces]);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* « PIÈCES » DEVIENT « DOCUMENTS » (Ines, 29/10/2026), et le TYPE se
          choisit d'abord, avant le fichier. Les clés techniques (`pieces`,
          `nature`) ne changent pas : l'adresse de l'onglet est un contrat. */}
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
        Les documents de l’appareil, rangés par type : notice, PV d’installation, certificat,
        mise à jour logicielle, facture… Les rapports rattachés à une intervention précise
        se trouvent avec cette intervention, dans la fiche de vie.
      </p>

      <DocumentsInternes e={e} />

      {pieces.length === 0 && (
        <EmptyState titre="Aucun fichier versé au dossier."
          indice="La notice du fournisseur et le PV d’installation sont les deux premières à verser : l’ISO 15189 §6.4.2 demande qu’elles soient accessibles au poste." />
      )}

      {NATURES.filter((n) => parNature.has(n.k)).map((n) => (
        <Bloc key={n.k} titre={`${n.label} (${parNature.get(n.k)!.length})`}>
          {parNature.get(n.k)!.map((p) => (
            <div key={p.id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '7px 0', borderTop: '1px solid var(--border-subtle)' }}>
              {/* Sans fichier, la pièce reste une RÉFÉRENCE utile : elle dit quel
                  document fait foi et où le trouver. Le taire donnerait à croire
                  qu'il n'y a rien. */}
              {p.nature === 'video' && p.url
                ? <VideoDemonstration url={p.url} libelle={p.libelle} />
                : p.cle
                ? <LienFichier f={p} />
                : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--text-muted)' }}>
                    <I n="doc" s={13} />référence seule — document non versé
                  </span>
                )}
              {p.reference && <span style={{ fontSize: 12.5, fontFamily: 'var(--font-mono)', color: 'var(--text-strong)' }}>{p.reference}</span>}
              {p.version && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>v{p.version}</span>}
              {p.libelle && <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>{p.libelle}</span>}
              {p.dateDocument && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>document du {fmtDate(p.dateDocument)}</span>}
              {/* Le nom d'arrivée, quand la convention l'a remplacé : c'est par
                  lui qu'on relie la pièce au courriel dont elle est sortie. */}
              {p.nomOrigine && p.nomOrigine !== p.name && (
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }} title="Nom du fichier à son arrivée, avant renommage">
                  reçu sous « {p.nomOrigine} »
                </span>
              )}
              {p.url && p.nature !== 'video' && (
                <a href={p.url} target="_blank" rel="noreferrer"
                  style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary-active)', textDecoration: 'none' }}>source ↗</a>
              )}
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>ajouté le {fmtDate(p.ajouteLe)}</span>
              {peutParc && (
                <ConfirmButton label="" titre="Supprimer ce document" icon="trash" variant="danger"
                  question={`Supprimer « ${p.name} » du dossier ? Il ne se récupère pas ; la suppression est tracée au journal.`} confirmer="Supprimer"
                  onConfirm={() => retirerPieceEquip(e.documentId, p.id)} />
              )}
            </div>
          ))}
        </Bloc>
      ))}

      {peutJoindre && (
        <div style={{ display: 'grid', gap: 8, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12 }}>
          <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>Ajouter un document</h3>
          {/* LE TYPE D'ABORD : c'est lui qui range le document et qui entre dans
              le nom du fichier versé. */}
          <div style={{ maxWidth: 360 }}>
            <label style={lbl} htmlFor="pj-nature">Type de document <span style={{ color: 'var(--red-600)' }}>*</span></label>
            <select id="pj-nature" value={nature} onChange={(ev) => setNature(ev.target.value)} style={inputStyle}>
              {NATURES.map((n) => <option key={n.k} value={n.k}>{n.label}</option>)}
            </select>
          </div>
          {/* Le fichier choisi, et son nom d'origine. Il n'est PAS encore versé :
              le dire évite qu'on quitte l'écran en croyant l'avoir déposé. */}
          {enAttente && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
              <I n="doc" s={13} />
              <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{enAttente.name}</span>
              <span style={{ color: 'var(--text-muted)' }}>({octets(enAttente.size)}) — pas encore versé</span>
              <Bouton taille="sm" onClick={raz}>Changer de fichier</Bouton>
            </div>
          )}
          {analyse && <BandeauMetadonnees a={analyse} />}

          <div style={GRILLE}>
            {/* La date du DOCUMENT, pas celle du dépôt. Sur un certificat c'est
                celle de l'étalonnage, et c'est elle qui entre au nom du fichier. */}
            <div>
              <label style={lbl} htmlFor="pj-date">Date du document{enAttente && <span style={{ color: 'var(--red-600)' }}> *</span>}</label>
              <input id="pj-date" type="date" value={meta.dateDocument}
                onChange={(ev) => setMeta({ ...meta, dateDocument: ev.target.value })} style={inputStyle} />
            </div>
            <div><label style={lbl} htmlFor="pj-ref">Référence du document</label>
              <input id="pj-ref" value={meta.reference} onChange={(ev) => setMeta({ ...meta, reference: ev.target.value })}
                placeholder="Ex. 994-909C" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
            <div><label style={lbl} htmlFor="pj-ver">Version / révision</label>
              <input id="pj-ver" value={meta.version} onChange={(ev) => setMeta({ ...meta, version: ev.target.value })}
                placeholder="Ex. 3.3.1" style={inputStyle} /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="pj-lib">Libellé{enAttente && <span style={{ color: 'var(--red-600)' }}> *</span>}</label>
              <input id="pj-lib" value={libelle} onChange={(ev) => setLibelle(ev.target.value)} placeholder="Ex. Notice d’utilisation — FR" style={inputStyle} /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="pj-url">Où trouver le document officiel</label>
              <input id="pj-url" value={meta.url} onChange={(ev) => setMeta({ ...meta, url: ev.target.value })}
                placeholder={nature === 'video' ? 'https://youtu.be/…' : 'https://dialog.roche.com — eLabDoc'} style={inputStyle} /></div>
          </div>

          {/* Le nom que la pièce PORTERA, montré avant de verser. Un nom qu'on
              découvre après coup est un nom qu'on ne discute plus. */}
          {enAttente && !manquantes.length && (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              Sera versé sous le nom{' '}
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-strong)' }}>
                {nomPieceApercu(e.code || '', nature, meta, enAttente.name)}
              </span>{' '}
              — le nom d’origine reste au journal.
            </span>
          )}
          {enAttente && manquantes.length > 0 && (
            <span style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>
              À compléter avant de verser : {manquantes.map((c) => c.label).join(', ')}.
            </span>
          )}

          {/* Deux gestes distincts : verser le fichier, ou seulement dire quel
              document fait foi et où il est. Le second est le cas RÉEL des
              notices constructeur, publiées sur les portails clients. */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {!enAttente && nature !== 'video' && (
              <BoutonJoindre label={`Charger : ${NATURES.find((n) => n.k === nature)?.label || nature}`} occupe={envoi}
                onFichier={(f) => {
                  setEnvoi(true);
                  setEnAttente(f);
                  void analyserPieceEquip(f)
                    .then((a) => {
                      setAnalyse(a);
                      // Ce qui a été LU remplit la case ; ce qui n'a pas été lu
                      // la laisse vide. Jamais de repli sur le nom du fichier :
                      // une date devinée a l'air d'une date lue.
                      if (a?.titre && !libelle.trim()) setLibelle(a.titre);
                      if (a?.dateDocument) setMeta((m) => ({ ...m, dateDocument: a.dateDocument }));
                    })
                    .finally(() => setEnvoi(false));
                }} />
            )}
            {enAttente && (
              <Bouton variante="primaire" taille="sm" onClick={() => {
                setEnvoi(true);
                void joindreFichierEquip(e.documentId, { type: 'piece', nature, libelle, ...meta }, enAttente)
                  .then((ok) => { if (ok) raz(); })
                  .finally(() => setEnvoi(false));
              }} disabled={envoi || manquantes.length > 0}>
                <I n="check" s={13} />{envoi ? 'Versement…' : 'Verser au dossier'}
              </Bouton>
            )}
            <Bouton taille="sm" onClick={() => {
              setEnvoi(true);
              void enregistrerReference(e.documentId, { nature, libelle, ...meta })
                .then((ok) => { if (ok) raz(); })
                .finally(() => setEnvoi(false));
            }} disabled={envoi || !!enAttente || !(meta.reference.trim() || meta.url.trim())}>
              {nature === 'video' ? 'Enregistrer le lien' : 'Enregistrer la référence seule'}
            </Bouton>
          </div>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            PDF, JPEG ou PNG — 25 Mo au plus. Le fichier est lu avant d’être versé : ce qu’il déclare
            (titre, date) remplit le formulaire, le reste se saisit. Sans fichier, indiquez au moins la
            référence ou l’adresse : une notice dont on ignore la version ne prouve pas qu’elle décrit
            l’appareil tel qu’il tourne.
          </span>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------- 4. étiquette */

/**
 * Étiquette d'inventaire — 62 × 40 mm, format des rouleaux Dymo / Brother.
 *
 * Les dimensions sont en millimètres et non en pixels : une étiquette dont la
 * taille dépendrait de la résolution d'écran ne collerait pas sur le support.
 * Le QR est encodé par le serveur (l'application n'embarque pas d'encodeur) et
 * pointe vers la fiche : le technicien scanne l'appareil devant lui et arrive
 * sur sa fiche de vie, ce qui est tout l'intérêt de l'étiquette.
 */
export function Etiquette({ e, onFerme }: { e: GedEquipement; onFerme: () => void }) {
  const { etiquetteEquip } = useGed();
  const [data, setData] = useState<GedEtiquette | null>(null);
  const [erreur, setErreur] = useState(false);
  const [exemplaires, setExemplaires] = useState(1);
  useEscape(true, onFerme);

  React.useEffect(() => {
    let vivant = true;
    void etiquetteEquip(e.documentId).then((d) => { if (vivant) { setData(d); setErreur(!d); } });
    return () => { vivant = false; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [e.documentId]);

  return (
    <div role="dialog" aria-modal="true" aria-label="Étiquette d’équipement"
      /* Fenêtre de CONSULTATION : le clic à côté referme, c'est le confort
         que la RQ a demandé de garder le 04/09/2026. Rien ne s'y saisit. */
      onClick={(ev) => { if (ev.target === ev.currentTarget) onFerme(); }}
      style={{ position: 'fixed', inset: 0, background: VOILE, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 400 }}>
      <div style={{ ...card, padding: 20, maxWidth: 560, width: '100%', maxHeight: '90vh', overflow: 'auto', display: 'grid', gap: 14 }}>
        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-strong)', flex: 1 }}>Étiquette d’inventaire</h2>
          <Bouton taille="sm" onClick={onFerme} aria-label="Fermer">Fermer</Bouton>
        </div>

        {erreur && <p style={{ margin: 0, fontSize: 13, color: 'var(--red-600)' }}>L’étiquette n’a pas pu être produite.</p>}
        {!data && !erreur && <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Génération du QR…</p>}

        {data && (
          <>
            <div className="no-print" style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <label style={lbl} htmlFor="et-nb">Exemplaires</label>
                <input id="et-nb" type="number" min={1} max={24} value={exemplaires}
                  onChange={(ev) => setExemplaires(Math.min(24, Math.max(1, parseInt(ev.target.value, 10) || 1)))}
                  style={{ ...inputStyle, width: 90 }} />
              </div>
              <Bouton variante="primaire" taille="sm" onClick={() => window.print()}>
                <I n="print" s={13} />Imprimer
              </Bouton>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)', flex: '1 1 180px' }}>
                62 × 40 mm. Réglez l’impression sur « taille réelle » (100 %), sans mise à l’échelle.
              </span>
            </div>

            {/* Zone d'impression : le reste de la page est masqué par globals.css */}
            <div id="ged-print-area" style={{ display: 'flex', flexWrap: 'wrap', gap: '4mm' }}>
              {Array.from({ length: exemplaires }, (_, i) => <Vignette key={i} d={data} />)}
            </div>

            <p className="no-print" style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', wordBreak: 'break-all' }}>
              Le QR ouvre : {data.cible}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Vignette({ d }: { d: GedEtiquette }) {
  const et = d.etiquette;
  const meta = vocabMeta(EQUIP_STATES, et.statut);
  return (
    <div style={{
      width: '62mm', height: '40mm', boxSizing: 'border-box', padding: '2.5mm',
      border: '0.3mm solid #000', borderRadius: '1.5mm', background: 'var(--surface-card)', color: '#000',
      display: 'flex', gap: '2mm', overflow: 'hidden', pageBreakInside: 'avoid',
    }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.6mm' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '1.5mm' }}>
          <span style={{ fontSize: '3.6mm', fontWeight: 800, fontFamily: 'monospace', lineHeight: 1 }}>{et.code || '—'}</span>
          {/* Un appareil de prêt doit se voir SUR la machine : c'est ce qui évite
              qu'on le traite comme le titulaire, et qu'on oublie de le rendre. */}
          {et.estPret && (
            <span style={{ fontSize: '2.1mm', fontWeight: 800, letterSpacing: '0.03em', border: '0.25mm solid #000', borderRadius: '0.8mm', padding: '0.2mm 1mm' }}>PRÊT</span>
          )}
        </div>
        <div style={{ fontSize: '2.7mm', fontWeight: 700, lineHeight: 1.15, overflow: 'hidden' }}>{et.nom}</div>
        <div style={{ fontSize: '2.2mm', lineHeight: 1.2, overflow: 'hidden' }}>{[et.marque, et.modele].filter(Boolean).join(' ')}</div>
        <div style={{ fontSize: '2.2mm', fontFamily: 'monospace' }}>N° {et.numeroSerie || '—'}</div>
        <div style={{ fontSize: '2.2mm' }}>{et.localisation || '—'}</div>
        <div style={{ flex: 1 }} />
        {/* L'échéance est la seule donnée VOLATILE de l'étiquette : elle porte
            donc sa date d'édition, faute de quoi on ne saurait pas si la
            vignette collée sur l'appareil est encore à jour. */}
        <div style={{ fontSize: '2.2mm' }}>
          Échéance : <strong style={{ fontFamily: 'monospace' }}>{et.prochaineEcheance ? fmtDate(et.prochaineEcheance) : 'non définie'}</strong>
        </div>
        {et.estPret && (
          <div style={{ fontSize: '2mm', fontWeight: 700 }}>
            Propriété {et.proprietaire || '—'}{et.restitutionPrevue ? ` · à rendre le ${fmtDate(et.restitutionPrevue)}` : ''}
          </div>
        )}
        <div style={{ fontSize: '1.9mm', color: 'var(--gray-800)' }}>{meta.label} · éditée le {fmtDate(et.editeeLe)}</div>
      </div>
      <div style={{ width: '17mm', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={d.qr} alt={`QR de la fiche ${et.code}`} style={{ width: '17mm', height: '17mm' }} />
        <div style={{ fontSize: '1.8mm', textAlign: 'center', marginTop: '0.8mm' }}>BIO24 Alliance</div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------- 5. NC liées */

/** Non-conformités rattachées à l'équipement, dans les deux sens du lien. */
/* ------------------------------------------------------------------------ */
/* Habilitations reliées à l'automate — 06/09/2026                          */
/* ------------------------------------------------------------------------ */

const NIVEAU_LABEL: Record<string, string> = { routine: 'Routine', avancee: 'Avancée', expert: 'Expert' };

/**
 * « Sur l'automate, je veux voir les critères d'habilitation reliés à cette
 * machine, et qui est habilité sur leur totalité. » La réciproque, côté
 * critères, de `PostesDeLEquipement`. Une personne qui détient l'une des
 * habilitations listées ici détient TOUS ses critères à ce niveau, ceux de
 * cet automate compris — c'est pourquoi « habilité sur la totalité » se lit
 * directement sur la liste des détenteurs, sans recalcul par critère.
 */
export function HabilitationsDeLEquipement({ documentId, onChange }: { documentId: string; onChange?: () => void }) {
  const roles = useGed((st) => st.session.roles);
  /* GÉRER DEPUIS L'APPAREIL (Ines, 28/10/2026) : rattacher, détacher, et les
     critères propres à l'appareil. RQ seul, comme le référentiel — le moteur
     le vérifie (`POST /ged/equip/:id/habilitations`). Habiliter une personne
     reste dans la matrice : c'est un acte signé, pas un réglage. */
  const peutGerer = roles.includes('rq');
  const [vue, setVue] = useState<{
    equipementCode?: string;
    habilitations: { code: string; libelle: string; niveaux: string[]; actif: boolean; criteres: string[] }[];
    habilites: Record<string, { orgId: string; nom: string; habilitation: string }[]>;
    note?: string;
  } | null>(null);
  const [erreur, setErreur] = useState('');
  const [tour, setTour] = useState(0);
  const [toutes, setToutes] = useState<{ code: string; libelle: string; actif: boolean }[]>([]);
  const [ajout, setAjout] = useState('');
  const [edition, setEdition] = useState<{ code: string; criteres: string[] } | null>(null);
  const [aDetacher, setADetacher] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/equip/${encodeURIComponent(documentId)}/habilitations`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!vivant) return; if (j) { setVue(j); setErreur(''); } else setErreur('Les habilitations n’ont pas pu être lues.'); })
      .catch(() => { if (vivant) setErreur('Les habilitations n’ont pas pu être lues.'); });
    return () => { vivant = false; };
  }, [documentId, tour]);

  useEffect(() => {
    if (!peutGerer) return;
    let vivant = true;
    void fetch('/api/ged/ged/rh/habilitations', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!vivant || !j) return;
        setToutes(((j.habilitations || []) as { code: string; libelle: string; actif: boolean }[])
          .map((h) => ({ code: h.code, libelle: h.libelle || h.code, actif: h.actif !== false })));
      })
      .catch(() => { /* le rattachement reste indisponible, la lecture non */ });
    return () => { vivant = false; };
  }, [peutGerer, tour]);

  const envoyer = async (corps: Record<string, unknown>, ok: string) => {
    setEnCours(true); setMessage('');
    const r = await fetchEcriture(`/api/ged/ged/equip/${encodeURIComponent(documentId)}/habilitations`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps),
    });
    const d = await r.json().catch(() => null);
    setEnCours(false);
    if (!r.ok) { setMessage(d?.error?.message || 'Modification refusée.'); return; }
    setMessage(ok); setAjout(''); setEdition(null); setADetacher(null);
    setTour((t) => t + 1);
    onChange?.();
  };

  if (erreur) return <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0 }}>{erreur}</p>;
  if (!vue) return <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>Lecture…</p>;

  const habs = vue.habilitations || [];
  const niveaux = Object.keys(vue.habilites || {});
  const rattachables = toutes.filter((h) => h.actif && !habs.some((x) => x.code === h.code));

  return (
    <div>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 8px', lineHeight: 1.5 }}>
        Les tâches d’habilitation qui nomment cet appareil, et qui en détient la totalité.
        {peutGerer
          ? ' C’est elles qui décident où l’appareil sert : il apparaît sur chaque poste qui exige l’une d’elles.'
          : ' Se règle depuis le référentiel des habilitations (Plannings & RH).'}
      </p>
      {!!vue.note && <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: '0 0 8px' }}>{vue.note}</p>}

      {!habs.length ? null : (
        <div style={{ display: 'grid', gap: 12, marginBottom: 10 }}>
          {habs.map((h) => (
            <div key={h.code}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 12.5 }}>{h.libelle}</strong>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>({h.code})</span>
                {!h.actif && <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}> — archivée</span>}
                {peutGerer && edition?.code !== h.code && aDetacher !== h.code && (
                  <>
                    <Bouton taille="sm" variante="discret" disabled={enCours}
                      onClick={() => { setEdition({ code: h.code, criteres: h.criteres.length ? [...h.criteres] : [''] }); setMessage(''); }}>
                      <I n="pen" s={12} /> Modifier les critères
                    </Bouton>
                    <Bouton taille="sm" variante="discret" disabled={enCours}
                      title={`Cet appareil ne sera plus couvert par « ${h.libelle} »`}
                      onClick={() => { setADetacher(h.code); setMessage(''); }}>
                      <I n="x" s={12} /> Détacher
                    </Bouton>
                  </>
                )}
              </div>
              {aDetacher === h.code && (
                <div style={{ display: 'grid', gap: 6, margin: '6px 0', maxWidth: 560 }}>
                  <span style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
                    « {h.libelle} » ne couvrira plus cet appareil : il quittera les postes qui ne le reçoivent que par
                    elle, et les personnes habilitées ne le seront plus sur lui à ce titre.
                    {h.criteres.length > 0 && <> Ses {h.criteres.length} critère(s) propre(s) à l’appareil seront retirés de l’habilitation.</>}
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Bouton taille="sm" onClick={() => setADetacher(null)} disabled={enCours}>Annuler</Bouton>
                    <Bouton taille="sm" variante="danger" disabled={enCours}
                      onClick={() => void envoyer({ detacher: h.code }, `Détaché de « ${h.libelle} ».`)}>Détacher</Bouton>
                  </div>
                </div>
              )}
              {edition?.code === h.code ? (
                <div style={{ display: 'grid', gap: 6, margin: '6px 0', maxWidth: 640 }}>
                  {edition.criteres.map((c, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6 }}>
                      <input aria-label={`Critère ${i + 1}`} value={c} style={{ ...inputStyle, flex: 1 }}
                        placeholder="Ex. : sait lancer une calibration et en lire le rapport"
                        onChange={(ev) => setEdition({ ...edition, criteres: edition.criteres.map((x, j) => (j === i ? ev.target.value : x)) })} />
                      <Bouton taille="sm" variante="discret" title="Retirer ce critère"
                        onClick={() => setEdition({ ...edition, criteres: edition.criteres.filter((_, j) => j !== i) })}>
                        <I n="x" s={12} />
                      </Bouton>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Bouton taille="sm" variante="discret" onClick={() => setEdition({ ...edition, criteres: [...edition.criteres, ''] })}>
                      <I n="plus" s={12} /> Ajouter un critère
                    </Bouton>
                    <span style={{ flex: 1 }} />
                    <Bouton taille="sm" onClick={() => setEdition(null)} disabled={enCours}>Annuler</Bouton>
                    <Bouton taille="sm" variante="primaire" disabled={enCours}
                      onClick={() => void envoyer({ habilitation: h.code, criteres: edition.criteres }, `Critères de « ${h.libelle} » enregistrés.`)}>
                      Enregistrer
                    </Bouton>
                  </div>
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                    Seuls les critères propres à cet appareil sont modifiés ici ; les critères transverses de
                    l’habilitation restent sur sa fiche.
                  </span>
                </div>
              ) : h.criteres.length ? (
                <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6 }}>
                  {h.criteres.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              ) : (
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                  Aucun critère propre à cet appareil n’est encore écrit sur cette habilitation.
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {peutGerer && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '4px 0 10px' }}>
          <Selecteur id={`hab-ajout-${documentId}`} value={ajout} onChange={(v) => { setAjout(v); setMessage(''); }}
            style={{ ...inputStyle, maxWidth: 360 }}>
            <option value="">Rattacher à une habilitation…</option>
            {rattachables.map((h) => <option key={h.code} value={h.code}>{h.libelle} ({h.code})</option>)}
          </Selecteur>
          <Bouton taille="sm" variante="primaire" disabled={!ajout || enCours}
            onClick={() => { const h = rattachables.find((x) => x.code === ajout); if (h) void envoyer({ rattacher: h.code }, `Rattaché à « ${h.libelle} ».`); }}>
            <I n="plus" s={12} /> Rattacher
          </Bouton>
        </div>
      )}
      {!!message && <p style={{ fontSize: 12.5, margin: '0 0 8px', fontWeight: 600 }}>{message}</p>}

      {!niveaux.length ? (
        habs.length ? (
          <p style={{ fontSize: 12.5, margin: 0, lineHeight: 1.5 }}>
            <strong>Personne n’est habilité</strong> sur les tâches ci-dessus, à aucun niveau.
          </p>
        ) : null
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          {['routine', 'avancee', 'expert'].filter((n) => vue.habilites[n]?.length).map((n) => (
            <p key={n} style={{ fontSize: 12.5, margin: 0, lineHeight: 1.6 }}>
              <strong>{NIVEAU_LABEL[n] || n} :</strong>{' '}
              {vue.habilites[n].map((p) => p.nom).join(', ')}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export function NCLiees({ e }: { e: GedEquipement }) {
  const { liens, ncs, ouvrirPanneau } = useGed();
  const code = e.code || '';
  // Lien du registre OU champ `equipementCode` de la NC — cf. `nc-equipement.ts`.
  const liees = useMemo(() => ncsDeLEquipement(ncs, liens, code), [ncs, liens, code]);
  if (!liees.length) return null;
  return (
    <Bloc titre={`Non-conformités rattachées (${liees.length})`}>
      {liees.map((n) => (
        // On entre dans la NC DEPUIS l'équipement : le chemin est retenu pour
        // que la fermeture ramène à cette fiche, et non au registre des NC.
        <button key={n.documentId} onClick={() => ouvrirPanneau({ type: 'nc', valeur: n.documentId, origine: code })} className="ged-row"
          style={{ display: 'flex', gap: 10, alignItems: 'baseline', width: '100%', textAlign: 'start', background: 'none', border: 'none', borderTop: '1px solid var(--border-subtle)', padding: '8px 0', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{n.ref}</span>
          <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text-body)' }}>{n.titre}</span>
          {pill(vocabMeta({ declaree: { label: 'Déclarée', tone: 'warning' }, encours: { label: 'En cours', tone: 'info' }, soldee: { label: 'Soldée', tone: 'success' }, cloturee: { label: 'Clôturée', tone: 'neutral' } }, n.statut))}
        </button>
      ))}
    </Bloc>
  );
}

/* ------------------------------------------- 6. le dossier, hors de l'écran */

/**
 * Le DOSSIER d'un équipement — exporté sous le nom `EquipementDossier`.
 *
 * L'assemblage à onglets vit dans `Equipements.tsx` (`DossierEquipement`) et y
 * reste : il compose des blocs qui n'appartiennent pas à ce fichier (mise en
 * service, maintenances, calibrations, cartes CQI, liens, journal). Ce
 * composant-ci REMPILE les blocs de ce fichier dans l'ordre de l'écran, pour
 * que la fiche d'un appareil soit lisible EN PANNEAU par-dessus la NC ou
 * l'action qui le met en cause, sans changer d'écran. Ce n'est donc pas un
 * second dossier : ce sont les mêmes blocs, non recopiés — les onglets, eux,
 * deviennent des sections empilées, parce qu'un panneau se parcourt en
 * défilant et qu'on n'y vient pas fouiller mais vérifier.
 *
 * `enPanneau` ne change rien au contenu : il retire le cadre de carte (le
 * panneau en fournit un) et masque ce qui QUITTERAIT l'écran — le panneau a son
 * propre « Ouvrir en pleine page » pour ça.
 */
export function EquipementDossier({ e, enPanneau = false }: { e: GedEquipement; enPanneau?: boolean }) {
  const etat = etatEquip(e.statut, e.miseEnService).cle;
  return (
    <div style={enPanneau
      ? { padding: '4px 2px', display: 'grid', gap: 18 }
      : { ...card, padding: 20, display: 'grid', gap: 18 }}>
      {/* En-tête : les repères dont on a besoin quelle que soit la section
          lue — comme dans le dossier de l'écran Équipements. */}
      <div style={{ display: 'grid', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {e.code && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{e.code}</span>}
          <BoutonJournal reference={e.code || ''} quoi="de cet équipement" />
          <h2 style={{ margin: 0, fontSize: 16.5, fontWeight: 700, color: 'var(--text-strong)', flex: 1 }}>{e.nom}</h2>
          {pill(vocabMeta(EQUIP_STATES, etat))}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {EQUIP_CATEGORIES.find((c) => c.key === e.categorie)?.label || e.categorie}
          {e.marque && <> · {e.marque}</>}
          {e.numeroSerie && <> · <span style={{ fontFamily: 'var(--font-mono)' }}>{e.numeroSerie}</span></>}
          {e.localisation && <> · {e.localisation}</>}
        </div>
      </div>

      <Identification e={e} />
      <FicheDeVie e={e} />
      <Pieces e={e} />
      {/* Chaque ligne de ce bloc est un DÉPART vers le registre des NC : en
          panneau, elle refermerait la superposition pour ouvrir ailleurs. Le
          bloc n'a donc pas sa place ici — on ouvre l'équipement en pleine page
          pour suivre ses non-conformités. */}
      {!enPanneau && <NCLiees e={e} />}
    </div>
  );
}
